import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import Header from './Header';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import ConversationSidebar from './ConversationSidebar';
import MobileMenuOverlay from './MobileMenuOverlay';
import ConfirmationDialog from './ConfirmationDialog';
import ShareDialog from './ShareDialog';
import MessageSearch from './MessageSearch';
import useInChatSearch from '../hooks/useInChatSearch';
import CreatedDocumentModal from './CreatedDocumentModal';
import RenameDuplicateModal from './RenameDuplicateModal';
import AttachPickerModal from './AttachPickerModal';
import { useChat } from '../hooks/useChat';
import { useApiStatus } from '../hooks/useApiStatus';
import { useFileAttachment, makeAttachmentDescriptor } from '../hooks/useFileAttachment';
import { useConversations } from '../hooks/useConversations';
import { useConversationDraft, patchVoiceSegmentInDraft, addAttachedFileToDraft, patchAttachedFileInDraft, removeAttachedFileFromDraft, readConversationDraft, clearConversationDraft, moveConversationDraft, appendTextToDraft } from '../hooks/useConversationDraft';
import {
    registerTranscriptionSink,
    hasInFlightTranscription,
    setAdoptedNewConversationId,
    readPendingSends,
    writePendingSends,
} from '../utils/backgroundTranscription';
import { markConversationResponded, clearConversationResponded } from '../hooks/useConversationResponses';
import useAuth from '../hooks/useAuth';
import apiService from '../services/apiService';
import { documentService } from '../services/documentService';
import { logger } from '../utils/logger';
import { isDocumentFile, isArchiveFile } from '../utils/constants';
import { mapDbMessages } from '../utils/messageMapper';
import {
    pendingTurnView, readResendMarker, writeResendMarker, PENDING_TURN_POLL_MS,
} from '../utils/pendingTurn';
import { touchConvOrder, pruneConvBuffers, removeConvBuffer } from '../utils/convBufferLru';
import { composeSnapshot } from '../utils/composeSnapshot';
import { patchDocumentCards } from '../utils/documentCardPatch';
import { patchCardResolution, patchSupersededCards } from '../utils/cardResolutionPatch';
import { channelDeepLink } from '../utils/searchChannels';
import { streamErrorFromError, streamErrorText, userErrorMessage } from '../utils/userError';
import { appendDictation } from '../utils/dictation';
import { useLanguage } from '../i18n';
// Slot floors (CHAT_MIN_WIDTH / PANEL_MIN_WIDTH / DIVIDER_WIDTH) live with the
// #432 column-fit rule in utils/slotGeometry.js.
import {
    CHAT_MIN_WIDTH,
    PANEL_MIN_WIDTH,
    DIVIDER_WIDTH,
    conversationColumnFits,
} from '../utils/slotGeometry';

// #368: how long the resume-on-remount pass waits for the shell to settle on a
// conversation before treating every queued send as off-screen. Only reached when
// there is nothing to restore — a restore resolves the effect immediately.
const RESUME_SETTLE_MS = 2500;

// #541 dictation: how long the composer's non-blocking hint stays up — the
// "Didn't catch that" nudge and a failed-dictation error sentence.
const DICTATION_EMPTY_NOTICE_MS = 3500;
const DICTATION_ERROR_NOTICE_MS = 6000;


/**
 * Vertical drag handle that sits between the chat area and the active panel
 * in split mode. Computes the new ratio relative to its parent container —
 * which is the post-conversation-sidebar wrapper inside this view — so the
 * sidebar / icon-rail widths never skew the math.
 */
const SplitDivider = ({ containerRef, onRatioChange }) => {
    const { t } = useLanguage();
    const draggingRef = useRef(false);
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        draggingRef.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        const handleMove = (ev) => {
            if (!draggingRef.current || !containerRef?.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const ratio = (rect.right - ev.clientX) / rect.width;
            onRatioChange(ratio);
        };
        const handleUp = () => {
            draggingRef.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
        };
        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleUp);
    }, [containerRef, onRatioChange]);
    return (
        <div
            onMouseDown={handleMouseDown}
            className="group w-[14px] flex-shrink-0 cursor-col-resize flex items-center justify-center"
            title={t('msgDragToResize', 'Drag to resize')}
            >
            <span className="w-[3px] h-10 rounded-full bg-white/0 group-hover:bg-white/25 transition-colors" />
            </div>
    );
};

/**
 * The always-live Main Office — chat messages, conversation sidebar, input
 * with drag-drop uploads / voice recording / draft autosave. Rendered by
 * WorkspaceShell. All panel state (Calendar/Expenses/Documents/Workspace/
 * Settings) lives in the shell — this view is focused purely on chat.
 */
// #448 re-open: what a reloaded chat does with a trailing unanswered user
// message (running vs never started, auto re-send, Resend; no expiry) lives in
// utils/pendingTurn.js.

export default function MainOfficeView({
    // The pathname occupant — what the pre-slot-model code called "the panel".
    // Still the right question for "is the URL currently a feature route?".
    activePanel,
    // The slot model (#361): the chat is one of at most two occupants, and
    // `featurePanels` is the ordered list of the others. See
    // `utils/workspaceSlots.js` for the rules and `WorkspaceShell` for the wiring.
    chatOpen = true,
    onCloseChat,
    featurePanels = [],
    expandedOccupant = null,
    splitRatio = 0.55,
    onSplitRatioChange,
    // Mobile Visual #171 — the app-like overlay menu. All this state is owned by
    // WorkspaceShell (it holds the panel toggles); conversation state lives here,
    // so the overlay is rendered here and fed the panel controls as props.
    isMobile = false,
    mobileMenuOpen = false,
    onOpenMobileMenu,
    onCloseMobileMenu,
    onOpenLauncher,
    onTogglePanel,
    onOpenSettings,
    activePanelName = null,
    // #437: features with something the user has not seen (the red dot).
    unseenPanels = [],
    onLogout,
    // #543: the identity the shell already holds — display fallback while this
    // component's own useAuth() is still reading the profile.
    sessionUser = null,
}) {
    const { t } = useLanguage();
    const splitContainerRef = useRef(null);
    // The app mounts WorkspaceShell under a single catch-all route ("/*"), so
    // useParams() never yields a :conversationId — deriving it from the pathname
    // is what actually makes deep links and Agenda "Shared Conversations" clicks
    // (which navigate to /chat/:id) load the right thread.
    const location = useLocation();
    const urlConversationId = useMemo(() => {
        const m = location.pathname.match(/^\/chat\/([^/]+)/);
        return m ? m[1] : null;
    }, [location.pathname]);
    const navigate = useNavigate();

    const [showSidebar, setShowSidebar] = useState(true);
    // #432 — the MOBILE shell's conversation sheet: the full sidebar (search,
    // per-conversation actions) as a full-screen layer, opened from the overlay.
    const [conversationSheetOpen, setConversationSheetOpen] = useState(false);
    // #432 — this view's own width, so the conversation column can fold away
    // when the slot floors cannot hold beside it (tablet-landscape widths).
    // Container-driven, like the panels' compact modes: a viewport breakpoint
    // cannot know how much of the screen the Features Sidebar already took.
    const [viewWidth, setViewWidth] = useState(0);
    const viewObserverRef = useRef(null);
    const viewRef = useCallback((node) => {
        if (viewObserverRef.current) { viewObserverRef.current.disconnect(); viewObserverRef.current = null; }
        if (!node || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver((entries) => {
            const w = Math.round(entries[0].borderBoxSize?.[0]?.inlineSize ?? entries[0].target.getBoundingClientRect().width);
            setViewWidth((prev) => (prev === w ? prev : w));
        });
        ro.observe(node);
        viewObserverRef.current = ro;
    }, []);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [conversationToDelete, setConversationToDelete] = useState(null);
    // #433: a delete that FAILED after the row had already left the list. The
    // hook puts the row back; this says why (auto-dismissing toast).
    const [deleteError, setDeleteError] = useState(null);
    useEffect(() => {
        if (!deleteError) return undefined;
        const timer = setTimeout(() => setDeleteError(null), 6000);
        return () => clearTimeout(timer);
    }, [deleteError]);
    const maxDocuments = 20;
    const [showSearch, setShowSearch] = useState(false);
    // FIFO queue of uploads parked on a duplicate-filename collision. A QUEUE (not
    // a single slot) so a batch with several colliding names doesn't have each
    // prompt supersede the last — every collision is resolved in turn and no pill
    // is left stuck "uploading" forever (Duplicate file names / batch collision bug).
    const [duplicateQueue, setDuplicateQueue] = useState([]);
    const [highlightedMessageId, setHighlightedMessageId] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [inChatSearchQuery, setInChatSearchQuery] = useState('');
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [isUploading] = useState(false);
    const [uploadedDocModal, setUploadedDocModal] = useState(null);
    const [, setUploadedDocsQueue] = useState([]);
    // "Select from File Manager" picker for the composer "+" menu (#208).
    const [chatDocPickerOpen, setChatDocPickerOpen] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);
    // Multi-segment voice composition: each entry is one recorded clip,
    // queued in the input tray while its transcription runs in the
    // background. Nothing is dispatched until the user clicks Send.
    const [voiceSegments, setVoiceSegments] = useState([]);
    // ── Dictation (#541) ────────────────────────────────────────────────────
    // The Main Chat mic no longer queues voice notes: a stopped clip is sent to
    // `/api/audio/transcribe` (mode=dictation) and its TEXT is appended to the
    // composer. `voiceSegments` above stays only so a draft saved BEFORE #541
    // (a queued clip) still restores, plays and sends. `dictatingKeys` holds the
    // conversations (id or '__new__') with a dictation in flight — the mic shows
    // "transcribing…" and Send waits there; other conversations are unaffected.
    const [dictatingKeys, setDictatingKeys] = useState(() => new Set());
    const setDictatingFor = useCallback((key, on) => {
        setDictatingKeys(prev => {
            if (on === prev.has(key)) return prev;
            const next = new Set(prev);
            if (on) next.add(key); else next.delete(key);
            return next;
        });
    }, []);
    // `{ key, kind: 'empty' | 'error', text }` — bound to the conversation it
    // belongs to, so a hint never shows up in a chat the user switched to.
    const [dictationNotice, setDictationNotice] = useState(null);
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);
    // ── Origin-bound "send once transcriptions settle" intent ─────────────
    // A single global `pendingSend` boolean tied to the on-screen composer LOST
    // the send when the user hit Send while a clip was transcribing and then
    // navigated away: the conversation-switch hydration replaced the component
    // voiceSegments, the origin's transcription resolved into the origin's DRAFT
    // (a path with no dispatch trigger), and the leftover boolean could misfire on
    // the new conversation. Instead we register the ORIGIN conversation id (or
    // '__new__') as "pending send"; whichever settles the last transcription — the
    // on-screen settle effect OR the off-screen draft resolver — flushes exactly
    // one dispatch for that origin, regardless of what's on screen. This is the
    // "Guaranteed Delivery" guarantee.
    //
    // #368: the registry is SEEDED FROM STORAGE and mirrored back on every
    // change. Component state alone died with the shell when the user navigated
    // to the launcher (`/home` is a route OUTSIDE WorkspaceShell, so the whole
    // tree unmounts) — the queued send was simply forgotten and the message was
    // never sent. Persisted, the intent survives the round-trip and flushes the
    // moment the shell is back.
    const [pendingSendConvIds, setPendingSendConvIds] = useState(() => readPendingSends());
    const pendingSendConvIdsRef = useRef(pendingSendConvIds);
    useEffect(() => { pendingSendConvIdsRef.current = pendingSendConvIds; }, [pendingSendConvIds]);
    const setPendingSendFor = useCallback((key, on) => {
        setPendingSendConvIds(prev => {
            if (on === prev.has(key)) return prev;
            const next = new Set(prev);
            if (on) next.add(key); else next.delete(key);
            // Mirror synchronously (not in an effect): `flushPendingOrigin`
            // claims an origin by mutating the ref + calling this, and that claim
            // has to be durable immediately — an unmount a tick later must not
            // resurrect a send that is already dispatching.
            writePendingSends(next);
            return next;
        });
    }, []);
    // ── '__new__' adoption (New conversations transcription bug #290) ────────
    // A voice send that STARTS a conversation (Send clicked from the blank state
    // while a clip is still transcribing) now mints the conversation row EAGERLY
    // at send-intent, so the sidebar shows "New Conversation" from the first
    // second instead of only once the transcription settles. The in-flight
    // transcription resolvers, however, captured a NULL origin at accept time —
    // this redirect sends them to the adopted conversation id so their patches
    // reach the renamed draft / live state and the pending send (now keyed on
    // the real id) actually flushes.
    // #368: the redirect lives in `utils/backgroundTranscription` rather than a
    // component ref, so a resolver that settles with the shell UNMOUNTED resolves
    // the same origin the mounted path would have.
    // Re-entrancy guard: a second Send click while the eager POST /conversations
    // is still in flight must not mint a SECOND conversation.
    const newConvAdoptionInFlightRef = useRef(false);
    // The conversation currently being fetched with NO warm buffer — drives the
    // skeleton so a cold switch never shows a blank/white panel (Issue 2).
    const [loadingConvId, setLoadingConvId] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingTimerRef = useRef(null);
    const recordingStartRef = useRef(0);

    const { token, user: liveUser, authStable } = useAuth();
    const user = liveUser || sessionUser || null;
    // System initials for the user's chat avatar (Toggles ticket) — same logic as
    // the IconRail corner avatar.
    const userInitials = (user?.username || user?.email || 'U').slice(0, 2).toUpperCase();
    const { apiStatus } = useApiStatus(apiService);
    const { messages, isLoading, messagesEndRef, setMessages, canStop, stopStreaming } = useChat();
    // Floating "Scroll to bottom" affordance (Arrow down ticket): shown only when
    // the user has scrolled up past a threshold from the latest message.
    const chatScrollRef = useRef(null);
    // In-chat find (#352). Owns the match set, the counter and the scrolling, read
    // straight off the rendered highlights inside `chatScrollRef`.
    const inChatSearch = useInChatSearch(chatScrollRef, inChatSearchQuery, messages);
    const [showScrollDown, setShowScrollDown] = useState(false);
    const handleChatScroll = useCallback(() => {
        const el = chatScrollRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        setShowScrollDown(distanceFromBottom > 200);
        chatScrollMemoRef.current = el.scrollTop;   // banked for a close/reopen (#361)
    }, []);
    const scrollChatToBottom = useCallback(() => {
        const el = chatScrollRef.current;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, []);
    // A CLOSED chat (#361) is `display:none`, which destroys the scrollport and
    // resets scrollTop to 0 — so the offset is banked on the way out and restored
    // on the way back in. The chat's real state (in-flight turns, buffers, drafts)
    // is React state and survives regardless; this is only about landing the user
    // where they left off, the same promise the COVERED (`invisible`) state keeps
    // for free.
    // The offset is banked on every scroll rather than in a cleanup: React commits
    // the DOM mutation BEFORE it runs effect cleanups, so by then the pane is
    // already `display:none` and its scrollTop already 0 — there is nothing left to
    // read. The scroll handler is the last moment the real value exists.
    const chatPaneRef = useRef(null);
    const chatScrollMemoRef = useRef(0);
    useEffect(() => {
        if (!chatOpen) return;
        const el = chatScrollRef.current;
        if (el && chatScrollMemoRef.current > 0 && el.scrollTop === 0) {
            el.scrollTop = chatScrollMemoRef.current;
        }
    }, [chatOpen]);
    // Recompute visibility whenever the thread changes (e.g. a conversation loads
    // not scrolled to the bottom, or a streamed message grows) — the scroll event
    // alone wouldn't fire in those cases.
    useEffect(() => {
        const id = setTimeout(() => handleChatScroll(), 60);
        return () => clearTimeout(id);
    }, [messages, handleChatScroll]);
    // Set of conversation IDs whose RAG response is currently in flight. Tracking
    // this PER CONVERSATION (rather than one global `ragStreaming` boolean) is what
    // makes the input controls obey "Parallel Isolation": Chat A streaming shows a
    // Stop button only in Chat A; Chat B keeps its Send + Record controls.
    const [processingConvIds, setProcessingConvIds] = useState(() => new Set());
    // Live mirror of processingConvIds for reads inside stable callbacks (e.g. the
    // buffer LRU pruner) that must never evict a conversation still streaming.
    const processingConvIdsRef = useRef(processingConvIds);
    useEffect(() => { processingConvIdsRef.current = processingConvIds; }, [processingConvIds]);
    // H-0.6: tool-step traces are kept PER CONVERSATION (convId -> steps[]),
    // mirroring processingConvIds/ragStopFnsRef. The old single global array
    // made parallel streams cross-contaminate: Chat A's persisted metadata
    // captured Chat B's tool events, and A finishing wiped B's live trace.
    const [ragToolStepsByConv, setRagToolStepsByConv] = useState({});
    const ragToolStepsByConvRef = useRef({});
    useEffect(() => { ragToolStepsByConvRef.current = ragToolStepsByConv; }, [ragToolStepsByConv]);
    const updateConvToolSteps = useCallback((convId, updater) => {
        if (!convId) return;
        setRagToolStepsByConv(prev => ({ ...prev, [convId]: updater(prev[convId] || []) }));
    }, []);
    const clearConvToolSteps = useCallback((convId) => {
        if (!convId) return;
        setRagToolStepsByConv(prev => {
            if (!(convId in prev)) return prev;
            const next = { ...prev };
            delete next[convId];
            return next;
        });
    }, []);
    // Map of conversationId -> stop() for the stream currently running in it, so a
    // Stop click only aborts the conversation the user is actually looking at.
    const ragStopFnsRef = useRef({});
    // #446 Phantom turns: the server-side run currently answering each
    // conversation — `{userDbId, runId}`. Cleared when a run finishes; left in
    // place when the user STOPS one, so a following edit-and-resend can make
    // sure that run is cancelled before the new one starts.
    const activeRunsRef = useRef({});
    // A Stop clicked BEFORE a turn has registered its stop function. The Stop
    // control appears the moment a send marks the conversation processing, but
    // handleRagMessage first awaits the user-message save — a click in that gap
    // used to find no stop function and silently do nothing, so the turn ran on.
    // handleRagMessage checks this right after that await and bows out.
    const pendingStopRef = useRef(new Set());
    const markConvProcessing = useCallback((convId, on) => {
        if (!convId) return;
        setProcessingConvIds(prev => {
            const next = new Set(prev);
            if (on) next.add(convId); else next.delete(convId);
            return next;
        });
    }, []);
    // Tracks the conversation the user is CURRENTLY viewing, so a background
    // RAG response that lands in a different conversation can flag a green dot
    // (Fire-and-Forget notification) instead of silently completing.
    const viewedConvIdRef = useRef(null);
    const {
        conversations,
        currentConversation,
        loading: conversationsLoading,
        createConversation,
        loadConversation,
        loadAllConversations,
        updateConversation,
        deleteConversation,
        bulkDeleteConversations,
        addMessageToConversation,
        selectConversation,
        clearCurrentConversation,
    } = useConversations(token, authStable);

    // Is the conversation the user is CURRENTLY viewing mid-response? Drives the
    // input bar: true → show Stop (Situation B); false → show Send + Record
    // (Situation A), even if OTHER conversations are still streaming.
    const currentConvProcessing = !!(currentConversation?.id && processingConvIds.has(currentConversation.id));
    // Does the conversation on screen have a queued send waiting on transcription?
    // Drives the composer's "Finalizing…" button spinner (per-conversation now).
    const currentPendingSend = pendingSendConvIds.has(currentConversation?.id || '__new__');
    // #541: a dictation is transcribing into THIS conversation's composer.
    const currentDictating = dictatingKeys.has(currentConversation?.id || '__new__');

    // ── Draft persistence ─────────────────────────────────────────────────
    // Draft survives reloads and conversation switches. Full state (text +
    // attachments + reply chips) is stored in localStorage keyed by conv id.
    const { hydrate: hydrateDraft, saveDraft, renameDraft } = useConversationDraft(currentConversation?.id || null);
    const initialDraftRef = useRef(null);
    if (initialDraftRef.current === null) initialDraftRef.current = hydrateDraft();
    const [input, setInputState] = useState(initialDraftRef.current.text || '');
    // Latest composer text, readable from an async dictation resolver (#541) —
    // the closure's `input` is stale by the time the transcription returns.
    const inputValueRef = useRef(input);
    inputValueRef.current = input;
    const [replyingToMessages, setReplyingToMessages] = useState(initialDraftRef.current.replyingToMessages || []);
    // referenced expenses from drag-into-input (Phase 5 — reference chip)
    const [referencedExpenses, setReferencedExpenses] = useState([]);
    const lastConvIdForDraftRef = useRef(currentConversation?.id || null);
    const hydratingRef = useRef(false);

    // Keep the "viewed conversation" ref in sync and clear its green dot the
    // instant the user opens a conversation that had a background response
    // waiting (Fire-and-Forget "Read" behaviour).
    useEffect(() => {
        const id = currentConversation?.id || null;
        viewedConvIdRef.current = id;
        if (id) clearConversationResponded(id);
    }, [currentConversation?.id]);

    // ── Per-conversation message buffer (Fire-and-Forget UI stability) ──────
    // The chat renders a SINGLE visible `messages` array for the conversation on
    // screen. Without a per-conversation buffer, sending in Chat A and navigating
    // away orphaned A's optimistic + streaming messages (they only lived in that
    // array, which switching to Chat B replaced) — so A's stream then wrote into
    // B's thread, and returning to A reloaded from the DB before the background
    // commit had landed → "ghosting". This ref holds the live messages per
    // conversation so an in-flight thread survives navigation.
    const convMessagesRef = useRef({});
    // Touch-order (oldest first) for the buffer above, used to LRU-cap it so a long
    // session doesn't accumulate every conversation's messages forever (F-5.5). We
    // never evict the conversation on screen or one still processing a turn.
    const convBufferOrderRef = useRef([]);
    // Seed/refresh a conversation's buffer position and prune the buffer down to the
    // LRU cap, protecting the active + all processing conversations.
    const touchAndPruneConvBuffers = useCallback((activeConvId) => {
        if (activeConvId) {
            convBufferOrderRef.current = touchConvOrder(convBufferOrderRef.current, activeConvId);
        }
        const protectedIds = new Set(processingConvIdsRef.current);
        if (activeConvId) protectedIds.add(activeConvId);
        const { order } = pruneConvBuffers(convMessagesRef.current, convBufferOrderRef.current, protectedIds);
        convBufferOrderRef.current = order;
    }, []);
    // In-flight upload AbortControllers keyed by their pill's tempId — lets the
    // pill's X cancel a hung/accidental upload at any time (Cancel Upload ticket).
    const uploadAbortersRef = useRef({});
    // The buffer is updated ONLY by intentional, conversation-scoped writes:
    //  • the stream — setConvMessages(streamConvId, …) keeps an in-flight turn's
    // messages current whether or not its conversation is on screen;
    //  • handleSelectConversation — seeds the buffer when a conversation loads.
    // We deliberately do NOT mirror the visible `messages` via a generic effect:
    // during an A→B switch, `messages` and the viewed-conversation id update in
    // separate steps, so an effect would race and could wipe A's in-flight buffer
    // with B's empty array (the ghosting we're fixing).
    //
    // Update a SPECIFIC conversation's messages (even one not on screen). Reflects
    // to the visible thread only when that conversation is the one being viewed —
    // so a background stream never disturbs the conversation in view.
    const setConvMessages = useCallback((convId, updater) => {
        if (!convId) { setMessages(updater); return; }
        const prev = convMessagesRef.current[convId] ?? [];
        const next = typeof updater === 'function' ? updater(prev) : updater;
        convMessagesRef.current[convId] = next;
        if (convId === viewedConvIdRef.current) setMessages(next);
    }, [setMessages]);

    // #457 / #448 re-open: the SERVER saves the assistant message now. When a
    // reply (or a "PAI was stopped" notice) lands that this tab did not stream
    // itself — the tab was reloaded mid-turn, the reader was dropped, or Stop
    // was pressed — reload the conversation from the database. Never while this
    // tab is streaming into it (the live buffer is the truth then).
    const refreshConversationFromServer = useCallback(async (convId) => {
        if (!convId) return;
        try {
            const conv = await apiService.getConversation(convId);
            if (!conv || !Array.isArray(conv.messages)) return;
            if (processingConvIdsRef.current.has(convId)) return;
            const chatMessages = mapDbMessages(conv);
            convMessagesRef.current[convId] = chatMessages;
            if (viewedConvIdRef.current === convId) setMessages(chatMessages);
        } catch (err) {
            logger.warn('CHAT', 'Could not refresh the conversation after a server-side save:', err);
        }
    }, [setMessages]);

    useEffect(() => {
        const onSaved = (e) => {
            const { conversation_id: cid, message_id: mid } = e?.detail || {};
            if (!cid) return;
            if (processingConvIdsRef.current.has(cid)) return;
            const buf = convMessagesRef.current[cid];
            // This tab already adopted that row (it streamed the turn itself).
            if (buf && mid && buf.some(m => m.dbId === mid)) return;
            if (viewedConvIdRef.current === cid || buf !== undefined) {
                refreshConversationFromServer(cid);
            }
        };
        window.addEventListener('omnis:assistant-message-saved', onSaved);
        return () => window.removeEventListener('omnis:assistant-message-saved', onSaved);
    }, [refreshConversationFromServer]);

    // #448 re-open: after a reload the trailing user message is either being
    // answered (the server stamped `run_started_at` on it) or was never run at
    // all (the reload landed between the save and the stream request). Running
    // → placeholder + poll, Resend after 3 min. Never started → re-sent ONCE
    // automatically ("Sending…"), Resend if that did not take. No expiry.
    // Only in a conversation this user owns (a shared one cannot run turns).
    const [pendingNow, setPendingNow] = useState(() => Date.now());
    const pendingEligible = !!(currentConversation && !currentConvProcessing && !isReadOnly
        && !currentConversation.is_shared_with_me);
    const trailingForPending = pendingEligible && messages.length ? messages[messages.length - 1] : null;
    const pendingView = trailingForPending
        ? pendingTurnView({
            messages,
            now: pendingNow,
            marker: trailingForPending.dbId ? readResendMarker(trailingForPending.dbId) : null,
        })
        : null;
    const pendingUserMsg = pendingView ? pendingView.message : null;
    const pendingConvId = pendingUserMsg ? currentConversation?.id : null;
    const pendingKey = pendingUserMsg ? pendingUserMsg.dbId : null;
    useEffect(() => {
        setPendingNow(Date.now());
        if (!pendingKey || !pendingConvId) return undefined;
        const id = setInterval(() => {
            setPendingNow(Date.now());
            refreshConversationFromServer(pendingConvId);
        }, PENDING_TURN_POLL_MS);
        return () => clearInterval(id);
    }, [pendingKey, pendingConvId, refreshConversationFromServer]);

    // Upload a single file into the conversation folder, updating its optimistic
    // pill. On a duplicate-filename collision (HTTP 409) it opens the rename modal
    // instead of failing the pill, so the user can pick a unique name and retry.
    // `originConvId` is the conversation the upload batch was started in — pill
    // updates are routed there (not the conversation on screen now) so a batch
    // started in A never bleeds into B if the user navigates away mid-upload.
    const uploadOneDroppedFile = async (file, folderId, tempId, originConvId) => {
        // Each in-flight upload gets an AbortController keyed by its pill's tempId,
        // so clicking the pill's X (even mid-upload/processing) genuinely cancels
        // the request instead of just hiding it (Cancel Upload ticket).
        const aborter = new AbortController();
        uploadAbortersRef.current[tempId] = aborter;
        try {
            // #513 — a ZIP is UNPACKED by the server into Conversation
            // Uploads/<zip name>/… (it used to come back as `archive_extracted`,
            // which this path could not use: the pill failed and nothing was
            // stored). The pill becomes the import's descriptor + summary.
            if (isArchiveFile(file.name)) {
                const res = await documentService.uploadChatArchive(file, originConvId, aborter.signal);
                if (!res?.descriptor?.archive_import) throw new Error(t('msgUploadNoDocumentId', 'Upload did not return a document id'));
                patchPillForConv(originConvId, tempId, {
                    ...res.descriptor,
                    summary: res.descriptor.summary || res.summary || null,
                    uploading: false,
                    upload_error: null,
                });
                return;
            }
            // #404 — `originConvId` is the conversation this attachment belongs
            // to, so the OCR of an attached image is billed to that turn's
            // Energy Receipt instead of landing unattributed. It is already the
            // id every pill update is routed to, so there is nothing new to get
            // wrong; the server verifies it and ignores '__new__'.
            const result = await documentService.uploadDocumentToFolder(
                file, folderId, null, null, aborter.signal, originConvId,
            );
            const doc = result?.document || result?.documents?.[0] || result;
            if (!doc?.id) throw new Error(t('msgUploadNoDocumentId', 'Upload did not return a document id'));
            patchPillForConv(originConvId, tempId, {
                id: doc.id,
                filename: doc.original_filename || doc.filename || file.name,
                mime_type: doc.mime_type || file.type,
                file_size: doc.file_size ?? file.size,
                is_image: (doc.mime_type || file.type || '').startsWith('image/'),
                uploading: false,
                upload_error: null,
            });
        } catch (err) {
            // User cancelled this upload via the pill's X — the pill is already gone,
            // so don't resurrect it with a "failed" state.
            if (err?.aborted || aborter.signal.aborted) {
                return;
            }
            if (err?.status === 409 || err?.code === 'duplicate_filename') {
                // Enqueue the parked file — the modal works through the queue one at
                // a time, so multiple collisions in one batch never overwrite each other.
                setDuplicateQueue((prev) => [...prev, {
                    file,
                    folderId,
                    tempId,
                    originConvId,
                    filename: file.name,
                    message: err?.detail || t('msgUploadDuplicateFilename', 'A document with this name already exists. Please provide a unique name.'),
                }]);
                return;
            }
            logger.error('UPLOAD', `Upload failed: ${file.name}`, err);
            patchPillForConv(originConvId, tempId, {
                uploading: false,
                upload_error: userErrorMessage(err, t),
            });
        } finally {
            delete uploadAbortersRef.current[tempId];
        }
    };

    const handleDroppedFiles = async (files) => {
        const supported = files.filter(f => isDocumentFile(f.name));
        if (supported.length === 0) return;

        // Snapshot the originating conversation so files still uploading when the
        // user switches conversations stay tied to where the batch was started.
        const originConvId = lastConvIdForDraftRef.current || null;

        let folderId = null;
        try {
            folderId = await documentService.ensureSystemFolder('conversation-uploads');
        } catch (err) {
            logger.error('UPLOAD', 'Failed to ensure Conversation Uploads folder:', err);
        }

        for (const file of supported) {
            const pending = makeAttachmentDescriptor(file);
            addPillForConv(originConvId, pending);
            await uploadOneDroppedFile(file, folderId, pending.tempId, originConvId);
        }
    };

    // Retry the FRONT-of-queue duplicate under a new name (rename-before-save flow).
    // Dequeue first, then retry — a fresh collision on the new name simply re-enqueues
    // it at the back, and the modal advances to the next parked file meanwhile.
    const handleRenameDuplicate = async (newName) => {
        const current = duplicateQueue[0];
        if (!current) return;
        const { file, folderId, tempId, originConvId } = current;
        setDuplicateQueue((prev) => prev.slice(1));
        const renamed = new File([file], newName, { type: file.type });
        patchPillForConv(originConvId, tempId, { uploading: true, upload_error: null, filename: newName });
        await uploadOneDroppedFile(renamed, folderId, tempId, originConvId);
    };

    // Reject the front-of-queue duplicate: drop its pill (no stuck loader) and advance.
    const handleCancelDuplicate = () => {
        const current = duplicateQueue[0];
        if (current) removePillForConv(current.originConvId, current.tempId);
        setDuplicateQueue((prev) => prev.slice(1));
    };

    const handleUploadedDocAction = () => {
        setUploadedDocModal(null);
        setTimeout(() => {
            setUploadedDocsQueue(prev => {
                if (prev.length > 0) {
                    setUploadedDocModal(prev[0]);
                    return prev.slice(1);
                }
                return prev;
            });
        }, 300);
    };

    const {
        attachedFiles,
        isDragging,
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
        handleDrop,
        addAttachedFile,
        updateAttachedFile,
        removeAttachment,
        clearAttachments,
        setAttachedFiles,
    } = useFileAttachment(handleDroppedFiles);

    // ── Origin-aware attachment pills (cross-conversation upload binding) ────
    // A bulk upload started in conversation A must stay bound to A even if the
    // user navigates to B while files are still uploading. We route every pill
    // add/patch/remove to the conversation the BATCH was started in: when the
    // user is still viewing that conversation we touch live React state (pills
    // animate in real time); when they've navigated away we write straight into
    // the origin conversation's persisted draft — the same mechanism the voice-
    // transcription resolver uses. This stops later files in A's batch from
    // "bleeding" into whatever conversation happens to be on screen.
    // `lastConvIdForDraftRef` is the conversation whose draft is currently loaded
    // into the live attachedFiles state.
    const isViewingConv = (convId) => (convId || null) === (lastConvIdForDraftRef.current || null);
    const addPillForConv = (originConvId, descriptor) => {
        if (isViewingConv(originConvId)) addAttachedFile(descriptor);
        else addAttachedFileToDraft(originConvId, descriptor);
    };
    const patchPillForConv = (originConvId, tempId, patch) => {
        if (isViewingConv(originConvId)) updateAttachedFile(tempId, patch);
        else patchAttachedFileInDraft(originConvId, tempId, patch);
    };
    const removePillForConv = (originConvId, tempId) => {
        if (isViewingConv(originConvId)) removeAttachment({ tempId });
        else removeAttachedFileFromDraft(originConvId, tempId);
    };

    // Remove an attachment pill from the composer. If its upload is still in
    // flight, abort the request first so a hung/accidental upload is genuinely
    // cancelled (not just hidden) and never bricks the input (Cancel Upload ticket).
    const handleRemoveAttachment = (descriptor) => {
        const tempId = descriptor?.tempId;
        if (tempId && uploadAbortersRef.current[tempId]) {
            try { uploadAbortersRef.current[tempId].abort(); } catch (_) { /* noop */ }
            delete uploadAbortersRef.current[tempId];
        }
        removeAttachment(descriptor);
    };

    // Single-click reset of the whole attachment queue (Excess Document Overflow
    // ticket): abort every in-flight upload, then drop all pills — so a huge drop
    // can be cleared at once instead of removing files one by one.
    const handleClearAllAttachments = () => {
        Object.values(uploadAbortersRef.current).forEach((a) => {
            try { a.abort(); } catch (_) { /* noop */ }
        });
        uploadAbortersRef.current = {};
        clearAttachments();
    };

    // "Select from File Manager" (composer "+" menu, #208): attach an EXISTING
    // document WITHOUT re-uploading. We build the standard attachment descriptor
    // directly from the doc row and push a COMPLETED pill (no upload lifecycle /
    // abort needed) — the send path just carries the descriptor's id, which the
    // backend already reads like any attachment. Origin-bound like every other
    // pill, and de-duped so selecting the same doc (or one already attached)
    // never double-attaches it.
    const handleSelectExistingDocument = (doc) => {
        if (!doc?.id) return;
        const originConvId = lastConvIdForDraftRef.current || null;
        // De-dup against whatever is currently attached (only meaningful while the
        // origin is on screen; a re-pick after navigating away is a rare no-harm).
        if (isViewingConv(originConvId) && attachedFiles.some((f) => f.id === doc.id)) return;
        const mime = doc.mime_type || 'application/octet-stream';
        const descriptor = {
            tempId: `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            id: doc.id,
            filename: doc.original_filename || doc.filename,
            mime_type: mime,
            file_size: doc.file_size ?? 0,
            is_image: mime.startsWith('image/') || !!doc.is_image,
            uploading: false,
            upload_error: null,
        };
        addPillForConv(originConvId, descriptor);
    };

    // ── Hydrate the composer from a conversation's draft ──────────────────
    // Read straight from localStorage by EXPLICIT id so the new conv's draft loads
    // even on the same synchronous tick a switch happens (React state from the hook
    // would still hold the old conv's draft, and the hook's convId ref only catches
    // up on a later effect). Updating `lastConvIdForDraftRef` here means the
    // fallback effect below bails — so a switch handler that calls this synchronously
    // gets an ATOMIC commit (header + messages + composer in one paint), while URL
    // deep-links / initial mount still hydrate via the effect.
    const hydrateComposerForConversation = useCallback((convId) => {
        lastConvIdForDraftRef.current = convId;
        hydratingRef.current = true;
        const fresh = readConversationDraft(convId);
        setInputState(fresh.text || '');
        setReplyingToMessages(fresh.replyingToMessages || []);
        setAttachedFiles(fresh.attachedFiles || []);
        // Restore queued voice segments scoped to THIS conversation. The persisted
        // shape drops the in-memory `blob` / `blobUrl` (server `audioUrl` is enough
        // to play ready segments back); still-in-flight rows render with their
        // spinner until the original resolver patches the draft.
        // #368: a row still marked `transcribing` with NO job running for it is
        // ORPHANED — the page was reloaded (or the tab restored) while the clip
        // was in flight, so the blob and the fetch are both gone and nothing will
        // ever settle it. It used to spin forever; now it is honestly marked
        // failed, in storage too, so the user can re-record instead of waiting.
        // A clip whose job is STILL running (the launcher round-trip this ticket
        // is about) keeps its spinner — `hasInFlightTranscription` is what tells
        // the two apart, and the runner only forgets a job after delivering it.
        const orphaned = (s) => s.status === 'transcribing' && !hasInFlightTranscription(s.id);
        (Array.isArray(fresh.voiceSegments) ? fresh.voiceSegments : [])
            .filter(orphaned)
            .forEach((s) => patchVoiceSegmentInDraft(convId, s.id, {
                status: 'failed',
                error: t('msgTranscriptionInterrupted', 'Transcription was interrupted — please record again'),
            }));
        const restoredSegments = Array.isArray(fresh.voiceSegments)
            ? fresh.voiceSegments.map((s) => ({
                id: s.id,
                blob: null,
                blobUrl: null,
                duration: s.duration || 0,
                mimeType: s.mimeType || 'audio/webm',
                status: orphaned(s) ? 'failed' : (s.status || 'ready'),
                audioUrl: s.audioUrl || null,
                gcsPath: s.gcsPath || null,
                transcription: s.transcription || null,
                // A pre-send correction typed before the user navigated away
                // survives the round-trip (transcription edit #252).
                editedTranscription: typeof s.editedTranscription === 'string' ? s.editedTranscription : null,
                showTranscription: !!s.showTranscription,
                error: s.error || null,
                originConversationId: s.originConversationId || convId,
            }))
            : [];
        setVoiceSegments(restoredSegments);
    }, [setAttachedFiles, t]);

    // Fallback hydration for paths that change currentConversation WITHOUT the
    // switch handler (URL deep-links, shared-conversation navigation, initial
    // mount). The handler-driven path pre-sets lastConvIdForDraftRef, so this bails.
    useEffect(() => {
        const convId = currentConversation?.id || null;
        if (lastConvIdForDraftRef.current === convId) return;
        hydrateComposerForConversation(convId);
    }, [currentConversation?.id, hydrateComposerForConversation]);

    // Wrap setInput to also sync to draft storage.
    const setInput = (val) => {
        const next = typeof val === 'function' ? val(input) : val;
        setInputState(next);
        saveDraft({ text: next });
    };

    // Sync attachments / replies to draft whenever they change. The sync
    // effect fires right after hydration too — we skip that one pass (the
    // settle tick) so we don't overwrite the just-loaded draft.
    useEffect(() => {
        if (hydratingRef.current) {
            hydratingRef.current = false;
            return;
        }
        saveDraft({ attachedFiles, replyingToMessages, voiceSegments });
    }, [attachedFiles, replyingToMessages, voiceSegments, saveDraft]);

    // ── Expense reference chip: listen for drops into the input ──────────
    useEffect(() => {
        const handleDropRef = (e) => {
            try {
                const raw = e.detail;
                if (!raw || !raw.expense_id) return;
                setReferencedExpenses((prev) => {
                    if (prev.some((r) => r.expense_id === raw.expense_id)) return prev;
                    return [...prev, raw];
                });
            } catch (_) { /* ignore */ }
        };
        window.addEventListener('omnis:expense-ref-dropped', handleDropRef);
        return () => window.removeEventListener('omnis:expense-ref-dropped', handleDropRef);
    }, []);

    // ── Document/folder reference chips from the FileManager modal ────────
    // The DocumentsModal backdrop fires `omnis:document-ref-dropped` when
    // the user drags a row out of the file manager. Single docs become one
    // attachment chip; folders are expanded recursively into their docs;
    // multi-selections are batched. The LLM sees these the same way it
    // sees freshly-uploaded attachments — by document id.
    useEffect(() => {
        const handleDocDrop = async (e) => {
            const payload = e.detail;
            if (!payload) return;

            const seenIds = new Set(attachedFiles.map((f) => f.id).filter(Boolean));
            const pushDoc = (id, filename, mime_type, file_size) => {
                if (!id || seenIds.has(id)) return;
                seenIds.add(id);
                addAttachedFile({
                    tempId: `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                    id,
                    filename: filename || t('msgAttachmentDefaultFilename', 'Document'),
                    mime_type: mime_type || 'application/octet-stream',
                    file_size: file_size || 0,
                    is_image: (mime_type || '').startsWith('image/'),
                    uploading: false,
                });
            };

            // Primary item (full info from the drag payload)
            if (payload.type === 'document') {
                pushDoc(payload.id, payload.name, payload.mime_type, payload.file_size);
            }

            // Additional selected docs (id-only — fallback filename)
            for (const id of (payload.selectedDocuments || [])) {
                if (id !== payload.id) pushDoc(id, null, null, null);
            }

            // Folder(s) — expand recursively to all contained docs
            const folderIds = [];
            if (payload.type === 'folder') folderIds.push(payload.id);
            for (const fid of (payload.selectedFolders || [])) {
                if (!folderIds.includes(fid)) folderIds.push(fid);
            }
            for (const fid of folderIds) {
                try {
                    const res = await documentService.getFolderContentsRecursive(fid);
                    for (const doc of (res?.documents || [])) {
                        pushDoc(doc.id, doc.filename, null, null);
                    }
                } catch (err) {
                    logger.error('CHAT', `Failed to expand folder ${fid}:`, err);
                }
            }
        };
        window.addEventListener('omnis:document-ref-dropped', handleDocDrop);
        return () => window.removeEventListener('omnis:document-ref-dropped', handleDocDrop);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see note above: 't' is intentionally omitted
    }, [attachedFiles, addAttachedFile]);

    // Keep the chat's document cards in step with a rename/move (#330).
    //
    // The DURABLE fix is server-side — GET /conversations/<id> hydrates every
    // card event from the live document row, so a reload is right by
    // construction. This closes the gap BEFORE that fetch: a conversation switch
    // repaints synchronously from the warm `convMessagesRef` buffer and would
    // otherwise flash the pre-rename name until the deferred reload reconciled.
    // Every BUFFERED conversation is patched, not just the visible one, because
    // a document can be referenced from several threads.
    useEffect(() => {
        const apply = (change) => {
            for (const convId of Object.keys(convMessagesRef.current)) {
                const prev = convMessagesRef.current[convId];
                const next = patchDocumentCards(prev, change);
                if (next !== prev) setConvMessages(convId, next);
            }
        };
        // Two producers with slightly different payload shapes: the UI dispatches
        // {id, name/folder_id}, while the cross-channel live feed (#251) re-emits
        // the backend payload {document_id, filename, folder_id}. Accept both.
        const onRenamed = (e) => {
            const d = e.detail || {};
            const id = d.id || d.document_id;
            const name = d.name || d.filename;
            if (id && name) apply({ documentId: id, name });
        };
        const onMoved = (e) => {
            const d = e.detail || {};
            const id = d.id || d.document_id;
            if (id && 'folder_id' in d) apply({ documentId: id, folderId: d.folder_id ?? null });
        };
        window.addEventListener('omnis:document-renamed', onRenamed);
        window.addEventListener('omnis:document-moved', onMoved);
        return () => {
            window.removeEventListener('omnis:document-renamed', onRenamed);
            window.removeEventListener('omnis:document-moved', onMoved);
        };
    }, [setConvMessages]);

    // Keep a resolved confirmation card resolved across a conversation switch (#507).
    // apiService.resolveCardState fires `omnis:card-resolved` once the server has
    // saved the outcome; patch it into the conversation's warm buffer so the
    // re-entry paint shows the final state instead of a live "pending" card until
    // the deferred re-fetch lands.
    useEffect(() => {
        const onResolved = (e) => {
            const d = e.detail || {};
            if (!d.conversationId || !d.messageId) return;
            const prev = convMessagesRef.current[d.conversationId];
            if (!prev) return;
            const next = patchCardResolution(prev, d);
            if (next !== prev) setConvMessages(d.conversationId, next);
        };
        window.addEventListener('omnis:card-resolved', onResolved);
        return () => window.removeEventListener('omnis:card-resolved', onResolved);
    }, [setConvMessages]);

    // #545: a corrected card replaced the request's earlier open card(s) — close
    // them in every buffered conversation (action ids are unique), so the old
    // card loses its buttons now; the stream (or, for a Telegram turn, the live
    // feed) names them, and the stamped metadata does the same on reload.
    useEffect(() => {
        const onSuperseded = (e) => {
            const d = e.detail || {};
            for (const [convId, prev] of Object.entries(convMessagesRef.current)) {
                const next = patchSupersededCards(prev, d);
                if (next !== prev) setConvMessages(convId, next);
            }
        };
        window.addEventListener('omnis:cards-superseded', onSuperseded);
        return () => window.removeEventListener('omnis:cards-superseded', onSuperseded);
    }, [setConvMessages]);

    // Build the hybrid payload from CURRENT (on-screen) composer state.
    const buildSendSnapshot = useCallback(() => composeSnapshot({
        input, attachedFiles, referencedExpenses, voiceSegments, replyingToMessages,
    }), [input, attachedFiles, referencedExpenses, voiceSegments, replyingToMessages]);

    // Reconstruct the same snapshot purely from a conversation's PERSISTED draft —
    // used to flush an origin-bound send after the user navigated away (the origin's
    // state no longer lives in component state, only in its draft). Referenced
    // expenses aren't persisted to drafts, so they're dropped for the navigate-away
    // flush (already true of any navigate-away today — not a new regression).
    const buildSnapshotFromDraft = useCallback((convId) => {
        const draft = readConversationDraft(convId);
        return composeSnapshot({
            input: draft.text || '',
            attachedFiles: Array.isArray(draft.attachedFiles) ? draft.attachedFiles : [],
            referencedExpenses: [],
            voiceSegments: Array.isArray(draft.voiceSegments) ? draft.voiceSegments : [],
            replyingToMessages: Array.isArray(draft.replyingToMessages) ? draft.replyingToMessages : [],
        });
    }, []);

    // #448 (Empty chat screen): the ONE place that makes a conversation the
    // app's "current" one across reloads — `/chat/<id>` in the URL (only when the
    // Main Office owns the URL; an open panel's URL wins) and the
    // `omnis:last-conversation-id` key the `/` restore reads. Every path that MINTS
    // a conversation on the user's behalf calls it the instant the row exists:
    // the text first-send used to skip it (only the #290 voice first-send did it),
    // so a reload right after the first message landed on `/` with nothing to
    // restore — an empty chat. `replace` so Back never returns to an empty `/`.
    const routeToConversation = useCallback((convId) => {
        if (!convId) return;
        if (!activePanel) {
            navigate(`/chat/${convId}`, { replace: true });
        }
        try {
            localStorage.setItem('omnis:last-conversation-id', convId);
        } catch (_) { /* ignore */ }
    }, [activePanel, navigate]);

    // Actually fire the dispatch for a SPECIFIC target conversation, which may not
    // be the one on screen (an origin-bound send flushed after the user navigated
    // away). `viewing` = the target is the conversation currently in view: when
    // true we clear the on-screen composer; when false we leave the current view
    // untouched and only clear the target's persisted draft. Either way the message
    // + streamed response are routed through the per-conversation buffer so they
    // land in the right thread and flag a green dot if the user has moved on.
    const dispatchSnapshotToConversation = useCallback(async (snap, targetConvId, viewing) => {
        if (!snap) return;
        const {
            typed, attachedDocuments, dispatchSegments, segmentBlobUrls,
            audioTranscriptions,
            displayContent, backendContent, replyingTo, referencedExpensesSnapshot,
        } = snap;

        if (viewing) {
            // Immediate on-screen feedback: empty the composer the instant Send fires.
            setInputState('');
            clearAttachments();
            setReplyingToMessages([]);
            setReferencedExpenses([]);
            setVoiceSegments([]);
        }
        // Defer blob-URL cleanup so the optimistic message can still play them
        // while the server URLs settle into the rendered bubble.
        setTimeout(() => {
            segmentBlobUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch (_) { /* ignore */ } });
        }, 5000);

        try {
            // Resolve the target conversation object. A real id → look it up (or a
            // bare stub is enough — handleRagMessage only needs `.id`). No id →
            // '__new__' send, mint a conversation.
            let activeConversation = null;
            if (targetConvId) {
                activeConversation = (currentConversation && currentConversation.id === targetConvId)
                    ? currentConversation
                    : (conversations.find(c => c.id === targetConvId) || { id: targetConvId });
            }
            if (!activeConversation) {
                if (viewing) {
                    // On-screen new chat: empty the '__new__' text BEFORE it migrates
                    // so the switch-hydration doesn't reload it (sticky-first-message
                    // bug), then adopt the scratch draft into the created conversation.
                    saveDraft({ text: '' });
                    activeConversation = await createConversation();
                    selectConversation(activeConversation);
                    renameDraft(activeConversation.id);
                    // #448: route + remember it NOW (before the user message is
                    // saved and the turn streams), exactly like the #290 voice
                    // first-send, so a reload lands on this conversation.
                    routeToConversation(activeConversation.id);
                } else {
                    // Off-screen '__new__' send (rare): mint a conversation and migrate
                    // the scratch draft to it WITHOUT stealing the user's current view.
                    activeConversation = await createConversation();
                    moveConversationDraft('__new__', activeConversation.id);
                }
            }
            // Clear the TARGET conversation's persisted draft by explicit id (origin
            // OR current) — so returning to it shows an empty composer, not a stuck
            // draft. Using the id (not the hook's convId-bound clearDraft) is what
            // makes the off-screen origin flush clear the RIGHT draft.
            clearConversationDraft(activeConversation.id);

            if (activeConversation?.id) {
                // A new turn starts clean: a Stop left over from an earlier turn
                // must never cancel this one.
                pendingStopRef.current.delete(activeConversation.id);
                markConvProcessing(activeConversation.id, true);
            }

            await handleRagMessage(
                backendContent,
                displayContent,
                activeConversation,
                false,
                replyingTo,
                attachedDocuments,
                referencedExpensesSnapshot,
                dispatchSegments.length > 0 ? dispatchSegments : null,
                {
                    text_content: typed || null,
                    audio_transcriptions: audioTranscriptions && audioTranscriptions.length > 0
                        ? audioTranscriptions
                        : null,
                },
            );
        } catch (error) {
            console.error('Error sending message:', error);
        }
        // `handleRagMessage` is intentionally omitted — including it would rebuild this
        // callback on every message-state change and defeat the stable-identity contract
        // its consumers rely on; it is referenced through a stable closure here.
    }, [ // eslint-disable-line react-hooks/exhaustive-deps
        currentConversation, conversations, createConversation, selectConversation,
        renameDraft, clearAttachments, saveDraft, markConvProcessing, routeToConversation,
    ]);

    // Flush a queued origin-bound send once its transcriptions have settled. This
    // is the SINGLE dispatch entry for a pending send — called by both the on-screen
    // settle effect (viewing the origin) and the off-screen transcription resolver
    // (navigated away). Membership in the registry is the single-dispatch guard:
    // we claim (remove) the origin synchronously before dispatching so a re-entrant
    // caller can't double-send. Kept in a ref so the resolver's captured closure
    // always calls the LATEST version (avoids stale-closure double/never sends).
    const flushPendingOrigin = useCallback((convIdRaw) => {
        const key = convIdRaw || '__new__';
        if (!pendingSendConvIdsRef.current.has(key)) return;
        const viewing = key === (lastConvIdForDraftRef.current || '__new__');
        const segs = viewing
            ? voiceSegments
            : (readConversationDraft(convIdRaw).voiceSegments || []);
        if (segs.some(s => s.status === 'transcribing')) return; // not all settled yet
        // Claim synchronously (single-dispatch guard) — mutate the ref before any
        // re-entrant effect run can read it, then reflect in state for the UI.
        const claimed = new Set(pendingSendConvIdsRef.current);
        claimed.delete(key);
        pendingSendConvIdsRef.current = claimed;
        setPendingSendFor(key, false);
        // #368: a clip that FAILED to transcribe contributes nothing to the
        // payload (`composeSnapshot` only dispatches `ready` segments), so firing
        // the queued send would post a message with the audio silently missing —
        // an empty bubble when the clip was the whole message. Drop the queued
        // intent instead and leave the composer populated: the user comes back to
        // a visibly failed clip they can re-record, not to a message they never
        // meant to send.
        if (segs.some(s => s.status === 'failed')) {
            logger.info('SEND', 'Queued send cancelled: a voice segment failed to transcribe.');
            return;
        }
        const snap = viewing ? buildSendSnapshot() : buildSnapshotFromDraft(convIdRaw);
        if (!snap) return;
        dispatchSnapshotToConversation(snap, convIdRaw || null, viewing);
    }, [voiceSegments, buildSendSnapshot, buildSnapshotFromDraft, dispatchSnapshotToConversation, setPendingSendFor]);
    const flushPendingOriginRef = useRef(flushPendingOrigin);
    useEffect(() => { flushPendingOriginRef.current = flushPendingOrigin; }, [flushPendingOrigin]);

    const handleSendMessage = async () => {
        // Block a second send only in the conversation that's already processing
        // (Situation B shows Stop, not Send). Other conversations remain sendable.
        if (currentConvProcessing) return;
        // #541: the dictated text is about to land in the box (~1 s) — sending
        // now would post the message without it.
        if (dictatingKeys.has(currentConversation?.id || '__new__')) {
            logger.info('SEND', 'Blocking send: dictation still transcribing');
            return;
        }
        if (attachedFiles.some(f => f.uploading)) {
            logger.info('SEND', 'Blocking send: attachments still uploading');
            return;
        }
        const snapshot = buildSendSnapshot();
        if (!snapshot) return;

        // Wait-and-send: if there are segments still transcribing, register this
        // conversation as "pending send". The intent is now bound to the ORIGIN
        // conversation (not the on-screen composer), so it survives the user
        // navigating away — the transcription resolver flushes it for the origin
        // regardless of what's on screen. The button shows a spinner meanwhile.
        if (voiceSegments.some(s => s.status === 'transcribing')) {
            let key = currentConversation?.id || '__new__';
            // #290 (New conversations transcription bug): a voice send that STARTS
            // a conversation must exist in the sidebar from the first second. With
            // no conversation yet ('__new__'), mint the row NOW — it renders as
            // "New Conversation" immediately (pinned via pendingSendConvIds below)
            // and the queued payload flushes into a real, visible conversation
            // instead of one that only comes into existence when the transcription
            // settles (during which the sidebar used to show nothing at all).
            if (key === '__new__') {
                if (newConvAdoptionInFlightRef.current) return; // first click's adoption still in flight
                newConvAdoptionInFlightRef.current = true;
                try {
                    const newConversation = await createConversation();
                    // Adopt: select it and migrate the '__new__' scratch draft
                    // WHOLE (text + queued segments) to the real id. Unlike the
                    // direct-dispatch path, the composer is NOT cleared here — the
                    // send hasn't dispatched yet, so the live state stays put and
                    // lastConvIdForDraftRef is pre-set so the hydration effect
                    // doesn't reload over it.
                    selectConversation(newConversation);
                    renameDraft(newConversation.id);
                    lastConvIdForDraftRef.current = newConversation.id;
                    setAdoptedNewConversationId(newConversation.id);
                    viewedConvIdRef.current = newConversation.id;
                    if (convMessagesRef.current[newConversation.id] === undefined) {
                        convMessagesRef.current[newConversation.id] = [];
                    }
                    routeToConversation(newConversation.id);
                    key = newConversation.id;
                } catch (error) {
                    // Fall back to the pre-#290 '__new__' queueing — the flush
                    // will mint the conversation at transcription-settle instead.
                    logger.error('SEND', 'Eager conversation creation for a pending voice send failed — falling back to __new__:', error);
                } finally {
                    newConvAdoptionInFlightRef.current = false;
                }
            }
            logger.info('SEND', 'Pending send: voice segments still transcribing — queued for origin, will auto-dispatch.');
            setPendingSendFor(key, true);
            return;
        }

        await dispatchSnapshotToConversation(snapshot, currentConversation?.id || null, true);
    };

    // On-screen settle check: when the conversation IN VIEW has a queued send and
    // its transcriptions have all settled, flush it. (The off-screen origin is
    // flushed by the transcription resolver instead — see handleAcceptRecording.)
    useEffect(() => {
        const key = currentConversation?.id || '__new__';
        if (!pendingSendConvIds.has(key)) return;
        if (voiceSegments.some(s => s.status === 'transcribing')) return;
        // #368: never dispatch into a thread that is still COLD. After a shell
        // remount (the launcher round-trip) `convMessagesRef` is empty, so the
        // composer hydrates — and this effect can fire — while `loadConversation`
        // is still fetching the history. Dispatching then appends the optimistic
        // message to an empty buffer, which the arriving DB snapshot defers to
        // (the buffer wins while the conversation is processing), leaving the
        // thread showing ONLY the new turn until the next full load. Waiting one
        // fetch is free: the effect re-runs when `loadingConvId` clears.
        if (loadingConvId && loadingConvId === key) return;
        flushPendingOriginRef.current(currentConversation?.id || null);
    }, [pendingSendConvIds, voiceSegments, currentConversation?.id, loadingConvId]);

    // #368 — Resume on remount. A send queued in conversation A and then
    // abandoned for the launcher comes back as a persisted intent (seeded into
    // `pendingSendConvIds` above) plus a draft the background runner has since
    // settled. If the user returns INTO A, the on-screen effect above flushes it
    // with A's real history loaded; this is the safety net for landing somewhere
    // else — the origin's message must still be delivered either way.
    // Deliberately skips the conversation in view so the two paths can never race
    // for the same origin (an off-screen flush clears the DRAFT, and the live
    // composer state would then re-persist the clip it just sent).
    const resumedPendingRef = useRef(false);
    useEffect(() => {
        if (resumedPendingRef.current) return;
        if (!authStable || conversationsLoading) return;
        const flushOffScreenOrigins = () => {
            if (resumedPendingRef.current) return;
            resumedPendingRef.current = true;
            const viewedKey = viewedConvIdRef.current || '__new__';
            Array.from(pendingSendConvIdsRef.current).forEach((key) => {
                if (key === viewedKey) return;
                flushPendingOriginRef.current(key === '__new__' ? null : key);
            });
        };
        // The last-used conversation is restored by a LATER effect in this same
        // flush, so `currentConversation` is still null the first time we land
        // here. Claiming the origin now would class it as off-screen and dispatch
        // into a cold buffer; wait for the restore — or, when there is nothing to
        // restore, a short grace period — so returning INTO the origin is handled
        // by the on-screen path instead.
        if (currentConversation?.id) { flushOffScreenOrigins(); return undefined; }
        const timer = setTimeout(flushOffScreenOrigins, RESUME_SETTLE_MS);
        return () => clearTimeout(timer);
    }, [authStable, conversationsLoading, currentConversation?.id]);

    // Common cleanup: stop timer + media tracks, clear refs.
    const finishRecorder = () => {
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
        const recorder = mediaRecorderRef.current;
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        setIsRecording(false);
        setRecordingDuration(0);
        return recorder;
    };

    const handleStartRecording = async () => {
        // #541: one dictation at a time per conversation, so the texts land in
        // the order they were spoken.
        if (dictatingKeys.has(currentConversation?.id || '__new__')) return;
        setDictationNotice(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : 'audio/webm';
            const mediaRecorder = new MediaRecorder(stream, { mimeType });
            audioChunksRef.current = [];
            mediaRecorderRef.current = mediaRecorder;
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) audioChunksRef.current.push(event.data);
            };
            mediaRecorder.onstop = () => {
                stream.getTracks().forEach(track => track.stop());
            };
            mediaRecorder.start();
            recordingStartRef.current = Date.now();
            setIsRecording(true);
            setRecordingDuration(0);
            recordingTimerRef.current = setInterval(() => {
                setRecordingDuration(prev => prev + 1);
            }, 1000);
        } catch (error) {
            logger.error('VOICE', 'Failed to start recording:', error);
        }
    };

    // Stops the recorder, drops the chunks. No segment is added — used by
    // the Discard (X) button while recording.
    const handleDiscardRecording = () => {
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state === 'inactive') {
            finishRecorder();
            return;
        }
        try { recorder.stop(); } catch (_) { /* ignore */ }
        finishRecorder();
    };

    // #541 Dictation: stops the recorder, sends the clip to the backend in
    // dictation mode (ONE round-trip, no audio kept) and APPENDS the returned
    // text to the composer — after whatever the user typed, never over it. The
    // result is a plain text draft the user can edit before pressing Send; the
    // sent message carries no `metadata.voice` / `voice_segments`.
    //
    // A direct await, not the durable background queue (#368): there is no
    // queued send to guarantee, only text to insert. Origin-bound like every
    // upload (Key Pattern 15): if the user switched conversation (or left the
    // shell) while it was transcribing, the text goes into the ORIGIN's draft,
    // never into whatever chat is open now.
    const handleAcceptRecording = async () => {
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state === 'inactive') {
            finishRecorder();
            return;
        }

        const blob = await new Promise((resolve) => {
            const origOnStop = recorder.onstop;
            recorder.onstop = () => {
                if (origOnStop) origOnStop();
                resolve(new Blob(audioChunksRef.current, { type: recorder.mimeType }));
            };
            try { recorder.stop(); } catch (_) { resolve(new Blob()); }
        });
        finishRecorder();

        if (!blob || blob.size === 0) return;

        const originConvId = lastConvIdForDraftRef.current || null;
        const key = originConvId || '__new__';
        setDictatingFor(key, true);
        setDictationNotice(prev => (prev && prev.key === key ? null : prev));

        let result = null;
        let failure = null;
        try {
            result = await documentService.dictate(blob, originConvId);
        } catch (err) {
            failure = err;
        }
        setDictatingFor(key, false);

        const viewingOrigin = isMountedRef.current
            && (lastConvIdForDraftRef.current || null) === originConvId;

        if (failure) {
            logger.error('VOICE', 'Dictation failed:', failure);
            // The functional error pattern (#439): the server's sentence for a
            // 4xx, the standard "please retry" otherwise. The box is untouched.
            if (viewingOrigin) {
                setDictationNotice({ key, kind: 'error', text: userErrorMessage(failure, t) });
            }
            return;
        }

        const text = result?.empty ? '' : (result?.transcription || '');
        if (!text) {
            if (viewingOrigin) {
                setDictationNotice({
                    key,
                    kind: 'empty',
                    text: t('dictationEmpty', 'Didn\'t catch that — try again'),
                });
            }
            return;
        }

        if (viewingOrigin) {
            const next = appendDictation(inputValueRef.current, text);
            inputValueRef.current = next;
            setInputState(next);
            saveDraft({ text: next });
        } else {
            appendTextToDraft(originConvId, text);
        }
    };

    // Auto-dismiss the dictation hint — it is a nudge, never a blocker.
    useEffect(() => {
        if (!dictationNotice) return undefined;
        const ms = dictationNotice.kind === 'error' ? DICTATION_ERROR_NOTICE_MS : DICTATION_EMPTY_NOTICE_MS;
        const timer = setTimeout(() => setDictationNotice(null), ms);
        return () => clearTimeout(timer);
    }, [dictationNotice]);

    // The mounted shell's half of the runner: route a settled transcription to
    // live React state (still viewing the origin) or to the origin's persisted
    // draft (navigated to another conversation). `effectiveOrigin` is resolved by
    // the module — including the #290 '__new__' adoption redirect — so the mounted
    // and unmounted paths can never disagree about which conversation a clip
    // belongs to. Stable identity (refs + stable setters only), so re-registering
    // is cheap and the runner never holds a stale closure.
    const applyTranscriptionPatch = useCallback((segmentId, patch, effectiveOrigin) => {
        const currentConvId = lastConvIdForDraftRef.current;
        if (currentConvId === effectiveOrigin) {
            setVoiceSegments(prev => prev.map(s => s.id === segmentId ? { ...s, ...patch } : s));
            // When viewing the origin, the on-screen settle effect handles a
            // queued send once setVoiceSegments re-renders — no direct flush.
        } else {
            // Touch the source draft so the segment appears updated
            // when the user returns. The orange sidebar dot stays
            // active via the draft-changed event the helper fires.
            patchVoiceSegmentInDraft(effectiveOrigin, segmentId, patch);
            // Guaranteed Delivery: if the user hit Send in this origin while it
            // was transcribing and then navigated away, dispatch the queued send
            // now that its transcription has settled — into the ORIGIN thread,
            // regardless of what's on screen. flushPendingOrigin no-ops unless
            // the origin actually has a pending send.
            flushPendingOriginRef.current(effectiveOrigin);
        }
    }, []);
    useEffect(() => registerTranscriptionSink(applyTranscriptionPatch), [applyTranscriptionPatch]);

    const handleRemoveSegment = (segmentId) => {
        setVoiceSegments(prev => {
            const target = prev.find(s => s.id === segmentId);
            if (target?.blobUrl) {
                try { URL.revokeObjectURL(target.blobUrl); } catch (_) { /* ignore */ }
            }
            return prev.filter(s => s.id !== segmentId);
        });
    };

    const handleToggleSegmentTranscription = (segmentId) => {
        setVoiceSegments(prev => prev.map(s => s.id === segmentId
            ? { ...s, showTranscription: !s.showTranscription }
            : s));
    };

    // Pre-send transcription editing (transcription edit #252) — the Main Office
    // parity with the Telegram bucket composer. The correction is kept
    // SEPARATE from the raw STT (`transcription`), so composeSnapshot can send the
    // #130/#228 contract: content/`audio_transcriptions` run on the correction,
    // while the stored segment keeps the faithful audio record under
    // `transcription` + the correction under `edited_transcription`. An untouched
    // clip carries no correction at all and sends as a standard voice note.
    // Editing is only reachable for the ON-SCREEN composer, and the draft-sync
    // effect persists it, so a half-corrected clip survives navigating away.
    const handleEditSegmentTranscription = (segmentId, text) => {
        setVoiceSegments(prev => prev.map(s => s.id === segmentId
            ? { ...s, editedTranscription: text }
            : s));
    };

    useEffect(() => {
        return () => {
            if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
        };
    }, []);

    // #439: shared error-state builders for the chat turn (see ChatErrorNotice).
    const streamErrorDisplayText = (se) => streamErrorText(se, t);
    const streamErrorFrom = (err, contentIsError) => streamErrorFromError(err, { contentIsError });

    const handleRagMessage = async (backendContent, displayContent, activeConversation, skipUserMessage = false, repliedToMessages = null, attachedDocuments = null, referencedExpensesArg = null, voiceSegmentsArg = null, hybridSources = null, turnOptions = null) => {
        // #240 Main Chat Timer: stamp the moment the user hits Send. The header's
        // total elapsed time is the WALL-CLOCK from here until the final response
        // begins to stream (first content token) — capturing LLM thinking + tool
        // execution + RAG retrieval, not just summed per-tool durations.
        const turnStartMs = Date.now();
        // This turn belongs to ONE conversation. Route every message update
        // through the per-conversation buffer (shadowing the component-level
        // setMessages) so a background turn never touches the conversation on
        // screen, and an in-flight turn survives the user navigating away + back.
        const streamConvId = activeConversation?.id || currentConversation?.id || null;
        const setMessages = (updater) => setConvMessages(streamConvId, updater);

        // Snapshot the TARGET conversation's prior history BEFORE we append the
        // optimistic user message — from its per-conversation buffer, not the
        // on-screen `messages`. For a background/origin-bound send the on-screen
        // thread belongs to a DIFFERENT conversation, so using `messages` would ship
        // the wrong conversation_context to the RAG endpoint. When viewing, the
        // buffer equals `messages`, so this is a no-op for the common path.
        const priorMessages = (streamConvId && convMessagesRef.current[streamConvId] !== undefined)
            ? convMessagesRef.current[streamConvId]
            : messages;

        // The reply selection is a SNAPSHOT taken at click time. A quick Reply on a
        // message that was still saving captured it without its dbId — and a quote
        // with no id can't jump to the original. By send time the save has usually
        // landed in the conversation buffer, so resolve the id there.
        const resolveDbId = (msg) => msg.dbId
            || priorMessages.find(m => m.id === msg.id)?.dbId
            || null;
        const repliedToData = repliedToMessages && repliedToMessages.length > 0
            ? repliedToMessages.map(msg => ({
                id: resolveDbId(msg),
                role: msg.isUser ? 'user' : 'assistant',
                content: msg.display || msg.content,
                attached_documents: msg.attachedDocuments || msg.metadata?.attached_documents || null,
            }))
            : null;

        // #446: the stored USER message this run answers — what Stop cancels.
        // A fresh send learns it from the save below; an edit re-run
        // (skipUserMessage) answers the last stored user message in the buffer.
        let streamUserDbId = skipUserMessage
            ? ([...priorMessages].reverse().find(m => m.isUser && m.dbId)?.dbId || null)
            : null;

        if (!skipUserMessage) {
            const userMessageId = Date.now();
            // When the user sends typed text AND voice in the same turn,
            // the typed half lives in hybridSources.text_content. We keep
            // it as a first-class field on the message (and persist it via
            // messageMetadata below) so Message.jsx can show it in the
            // bubble alongside the voice player — without it, the typed
            // text was invisible even though the LLM saw it.
            const typedText = hybridSources?.text_content || null;
            const userMessage = {
                id: userMessageId,
                content: displayContent,
                timestamp: new Date(),
                isUser: true,
                attachedDocuments: attachedDocuments && attachedDocuments.length > 0 ? attachedDocuments : undefined,
                referencedExpenses: referencedExpensesArg && referencedExpensesArg.length > 0 ? referencedExpensesArg : undefined,
                repliedTo: repliedToData,
                voiceSegments: voiceSegmentsArg && voiceSegmentsArg.length > 0 ? voiceSegmentsArg : undefined,
                typedText: typedText,
            };
            setMessages(prev => [...prev, userMessage]);

            try {
                const messageMetadata = {};
                if (repliedToData) messageMetadata.repliedTo = repliedToData;
                if (attachedDocuments && attachedDocuments.length > 0) messageMetadata.attached_documents = attachedDocuments;
                if (referencedExpensesArg && referencedExpensesArg.length > 0) messageMetadata.referenced_expenses = referencedExpensesArg;
                if (voiceSegmentsArg && voiceSegmentsArg.length > 0) messageMetadata.voice_segments = voiceSegmentsArg;
                if (typedText) messageMetadata.typed_text = typedText;
                const savedMessage = await addMessageToConversation(
                    activeConversation.id,
                    userMessage.content,
                    'user',
                    Object.keys(messageMetadata).length > 0 ? messageMetadata : null,
                );
                if (savedMessage?.id) {
                    streamUserDbId = savedMessage.id;
                    setMessages(prev => prev.map(msg =>
                        msg.id === userMessageId ? { ...msg, dbId: savedMessage.id } : msg
                    ));
                }
            } catch (error) {
                console.error('Error saving user message:', error);
            }
        }

        // Stop was clicked while the user message was still saving (see
        // pendingStopRef): the turn never starts. The saved user message stays,
        // exactly as if Stop had landed before the first token.
        if (streamConvId && pendingStopRef.current.has(streamConvId)) {
            pendingStopRef.current.delete(streamConvId);
            markConvProcessing(streamConvId, false);
            // #457 / #448 re-open: no run ever started. The SERVER saves the
            // "PAI was stopped by …" notice (cancel-run without a run id, before
            // any run): the browser only shows it at once and adopts its id. A
            // stream request still in flight elsewhere adopts the same notice.
            const stoppedBy = user?.username || null;
            const notice = {
                id: Date.now() + 1,
                content: `PAI was stopped by ${stoppedBy || 'you'}.`,
                timestamp: new Date(),
                isUser: false,
                metadata: { stopped: { run_id: null, stopped_by: stoppedBy, at: new Date().toISOString(), local: true }, partial_text: null },
            };
            setMessages(prev => [...prev, notice]);
            if (activeConversation && streamUserDbId) {
                try {
                    const res = await apiService.cancelRun(activeConversation.id, streamUserDbId, null);
                    if (res?.stopped_notice_id) {
                        setMessages(prev => prev.map(m => (m.id === notice.id ? { ...m, dbId: res.stopped_notice_id } : m)));
                    }
                    setTimeout(() => refreshConversationFromServer(activeConversation.id), 50);
                } catch (err) {
                    logger.warn('CHAT', 'Could not record the stop before the run started:', err);
                }
            }
            return;
        }

        // #457: a "PAI was stopped by …" notice is not something PAI said.
        // #457: set once the server announced the run — from then on the SERVER
        // owns saving this turn's reply, so a dropped connection must not make
        // the browser save a partial/error copy of its own.
        let runIdSeen = null;
        // `id` (#504): the BE looks the saved message up to tell the model which
        // confirmation cards on it were already confirmed/declined.
        const conversationContext = priorMessages.filter(msg => !msg.metadata?.stopped).map(msg => ({
            id: msg.dbId || null,
            role: msg.isUser ? 'user' : 'assistant',
            content: msg.content,
            attached_documents: msg.attachedDocuments || msg.metadata?.attached_documents || null,
        }));

        const ragOptions = {
            conversation_context: conversationContext,
            conversation_id: activeConversation?.id || currentConversation?.id || null,
            max_documents: maxDocuments,
            attached_documents: attachedDocuments && attachedDocuments.length > 0 ? attachedDocuments : null,
            referenced_expenses: referencedExpensesArg && referencedExpensesArg.length > 0 ? referencedExpensesArg : null,
            // `id` (the stored message id) lets the BE resolve what the quote
            // CARRIED server-side — e.g. create_reminder(attach_replied_media)
            // attaching a quoted photo or voice note (#386). The BE re-checks it
            // against the user's own conversations before reading anything.
            replied_to_messages: repliedToMessages && repliedToMessages.length > 0
                ? repliedToMessages.map(msg => ({
                    id: resolveDbId(msg),
                    role: msg.isUser ? 'user' : 'assistant',
                    content: msg.content,
                    attached_documents: msg.attachedDocuments || msg.metadata?.attached_documents || null,
                }))
                : null,
            // Hybrid payload (spec §3 "Hybrid Payload Composition"):
            // explicitly hand the BE the typed text and each voice
            // transcription as discrete fields so it can rewrite the
            // user-turn prompt with clear source separation.
            text_content: hybridSources?.text_content || null,
            audio_transcriptions: hybridSources?.audio_transcriptions || null,
            // #446: lets Stop cancel the run on the server (documentService
            // fires POST …/cancel-run when the stream is stopped or aborted).
            user_message_id: streamUserDbId,
            // #448 re-open: an automatic re-send of a turn that never started
            // must not become a second run if the original did reach the server.
            resend_only_if_not_started: !!turnOptions?.onlyIfNotStarted,
            onRunId: (runId) => {
                runIdSeen = runId;
                const entry = activeRunsRef.current[streamConvId];
                if (entry && entry.userDbId === streamUserDbId) entry.runId = runId;
            },
        };
        if (streamConvId && streamUserDbId) {
            activeRunsRef.current[streamConvId] = { userDbId: streamUserDbId, runId: null };
        }
        const clearActiveRun = () => {
            const entry = activeRunsRef.current[streamConvId];
            if (entry && entry.userDbId === streamUserDbId) delete activeRunsRef.current[streamConvId];
        };

        const assistantMessageId = Date.now() + 1;
        const assistantMessage = {
            id: assistantMessageId,
            content: '',
            timestamp: new Date(),
            isUser: false,
            isSearching: true,
            searchingText: '💭 Analyzing your request...',
            // #240: the header ticks live off this while processing (now − procStartMs);
            // it freezes to `processingMs` once the first response token streams.
            procStartMs: turnStartMs,
        };
        // streamConvId + the per-conversation setMessages shadow are defined at
        // the top of this turn. markConvProcessing scopes the Stop button to this
        // conversation only.
        setMessages(prev => [...prev, assistantMessage]);
        // #446: a new run starts with an empty trace. A STOPPED run never reaches
        // onComplete (which is what clears the bucket), so without this its tool
        // chips were inherited by — and persisted into — the next answer.
        clearConvToolSteps(streamConvId);
        markConvProcessing(streamConvId, true);

        let fullContent = '';
        let isRagStopped = false;
        let streamStopFunction = null;
        const abortController = new AbortController();
        // #240 Main Chat Timer: turnProcessingMs is the total shown in the
        // ThinkingSteps header and persisted (metadata.processing_ms) for reload.
        //
        // #328 moved WHERE it freezes. It used to freeze on the FIRST content
        // token, because the first token WAS the end of processing: nothing was
        // said until the whole turn had finished thinking and calling tools. Now
        // a turn can write a lead-in ("I'll check your calendar."), run two tool
        // rounds and keep writing — so the first token arrives ~2s in while the
        // turn runs on for another five, and freezing there would display a
        // number that is simply wrong. It now freezes when the turn COMPLETES,
        // which is what "total time" always meant: the wall clock from Send
        // until PAI stopped working. The header still ticks live until then.
        let turnProcessingMs = null;

        // H-0.5: the old "safety timeout" fired 30s after stream START and saved
        // the still-streaming partial message — every agentic turn longer than
        // 30s was saved TWICE (a truncated, metadata-less copy + the real one
        // from onComplete). Replaced with a SILENCE watchdog: re-armed on every
        // token/tool event, it fires only after 150s of no stream activity
        // (deliberately > the provider's 120s request timeout, so a hung backend
        // call hits its own timeout/retry first). `assistantSaved` is the single
        // shared flag guaranteeing EXACTLY ONE DB save across watchdog/onComplete/
        // onError, whichever runs first.
        let ragMetadata = null;
        let assistantSaved = false;
        let watchdogTimer = null;
        const WATCHDOG_SILENCE_MS = 150000;
        const armWatchdog = () => {
            if (watchdogTimer) clearTimeout(watchdogTimer);
            // #457: the SERVER saves the reply now, so the watchdog no longer
            // saves anything (that would be a second row). It drops this tab's
            // reader WITHOUT cancelling the run and reloads the conversation;
            // whatever the server saved (or saves later) appears through the
            // pending-turn placeholder / the live `assistant_message_saved` event.
            watchdogTimer = setTimeout(() => {
                if (assistantSaved || isRagStopped) return;
                logger.warn('CHAT', 'RAG silence watchdog fired (150s no activity) — dropping the reader; the server saves the reply');
                isRagStopped = true;
                clearConvToolSteps(streamConvId);
                markConvProcessing(streamConvId, false);
                delete ragStopFnsRef.current[streamConvId];
                clearActiveRun();
                if (streamStopFunction) streamStopFunction({ cancelServer: false });
                setTimeout(() => refreshConversationFromServer(streamConvId), 50);
            }, WATCHDOG_SILENCE_MS);
        };

        // #457: what Stop leaves on screen at once — the "PAI was stopped by …"
        // notice, with whatever text had streamed. The server saves the same
        // notice (with View Energy) and the live event swaps it in.
        const stoppedBubble = (msg) => ({
            ...msg,
            content: fullContent,
            isSearching: false,
            metadata: {
                ...(msg.metadata || {}),
                stopped: { run_id: null, stopped_by: user?.username || null, local: true },
                partial_text: fullContent || null,
            },
        });

        ragStopFnsRef.current[streamConvId] = () => {
            if (watchdogTimer) clearTimeout(watchdogTimer);
            isRagStopped = true;
            abortController.abort();
            clearConvToolSteps(streamConvId);
            markConvProcessing(streamConvId, false);
            delete ragStopFnsRef.current[streamConvId];
            if (streamStopFunction) streamStopFunction();
            setMessages(prev => prev.map(msg =>
                msg.id === assistantMessageId ? stoppedBubble(msg) : msg
            ));
        };

        try {
            const stopFunction = await documentService.ragQueryStream(
                backendContent,
                ragOptions,
                (token) => {
                    if (isRagStopped) return;
                    armWatchdog(); // token received = stream alive (H-0.5)
                    fullContent += token;
                    setMessages(prev => prev.map(msg =>
                        msg.id === assistantMessageId
                            ? { ...msg, content: fullContent, isSearching: false }
                            : msg
                    ));
                },
                async (metadata) => {
                    logger.info('CHAT', 'RAG onComplete callback triggered with metadata:', metadata);
                    if (watchdogTimer) clearTimeout(watchdogTimer);
                    // #448 re-open: the re-send found a run already started for
                    // this message (another tab, or the original request): drop
                    // our empty bubble; the pending placeholder waits for that run.
                    if (metadata && metadata.run_already_started) {
                        markConvProcessing(streamConvId, false);
                        delete ragStopFnsRef.current[streamConvId];
                        clearActiveRun();
                        clearConvToolSteps(streamConvId);
                        assistantSaved = true;
                        setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
                        setTimeout(() => refreshConversationFromServer(streamConvId), 50);
                        return;
                    }
                    // #457: the server saved this reply itself and told us its id.
                    let serverSavedId = null;
                    let serverStopped = false;
                    if (metadata && metadata.assistant_message_id) {
                        const { assistant_message_id: savedId, server_stopped: stoppedFlag, ...rest } = metadata;
                        serverSavedId = savedId;
                        serverStopped = !!stoppedFlag;
                        metadata = rest;
                    }
                    // #328: stop the clock BEFORE the 100ms socket-teardown yield
                    // below, so the figure measures PAI's work and not our own
                    // bookkeeping.
                    if (turnProcessingMs === null) turnProcessingMs = Date.now() - turnStartMs;
                    await new Promise(resolve => setTimeout(resolve, 100));
                    markConvProcessing(streamConvId, false);
                    delete ragStopFnsRef.current[streamConvId];
                    clearActiveRun();
                    // F-5.2 #1: the SSE parser sets metadata.truncated when the stream
                    // ended without a [DONE]/finish_reason terminator (dropped connection).
                    // Append a visible marker so the partial answer is saved HONESTLY as
                    // incomplete rather than passed off as a clean, complete reply.
                    // #439: the backend's error chunk arrives as metadata.stream_error
                    // (never as answer text). Delivered words stay; the error renders
                    // as its own notice. With nothing delivered, the persisted content
                    // is the display sentence, flagged so it is not shown twice.
                    const streamError = metadata?.stream_error
                        ? { ...metadata.stream_error, content_is_error: !fullContent.trim() }
                        : null;
                    const wasTruncated = !streamError && !!metadata?.truncated;
                    const savedContent = streamError && !fullContent.trim()
                        ? streamErrorDisplayText(streamError)
                        : wasTruncated && fullContent.trim()
                            ? `${fullContent}\n\n*(Response may be incomplete — connection dropped)*`
                            : fullContent;
                    if (streamError) {
                        metadata = { ...metadata, stream_error: streamError };
                    }
                    if (metadata) {
                        // H-0.6: only THIS conversation's steps — never another
                        // parallel stream's — get persisted into the metadata.
                        const finalSteps = ragToolStepsByConvRef.current[streamConvId] || [];
                        ragMetadata = { ...metadata, toolSteps: finalSteps, processing_ms: turnProcessingMs };
                        setMessages(prev => prev.map(msg =>
                            msg.id === assistantMessageId
                                ? { ...msg, content: (wasTruncated || streamError) ? savedContent : msg.content, isSearching: false, metadata: ragMetadata, toolSteps: finalSteps, processingMs: turnProcessingMs }
                                : msg
                        ));
                    }
                    clearConvToolSteps(streamConvId);
                    if (serverSavedId && !isRagStopped) {
                        // Adopt the server's row — never POST a second copy.
                        assistantSaved = true;
                        setMessages(prev => prev.map(msg =>
                            msg.id === assistantMessageId ? { ...msg, dbId: serverSavedId } : msg
                        ));
                        if (activeConversation && activeConversation.id !== viewedConvIdRef.current) {
                            markConversationResponded(activeConversation.id);
                        }
                        // The run was stopped at the very end (Stop landed while the
                        // last call streamed): show the saved notice, not the answer.
                        if (serverStopped) {
                            setTimeout(() => refreshConversationFromServer(streamConvId), 50);
                        }
                    } else if (wasTruncated && runIdSeen) {
                        // The connection dropped mid-turn. The server is still
                        // finishing it and will save the reply; show what the
                        // database has (the pending-turn placeholder takes over).
                        assistantSaved = true;
                        setTimeout(() => refreshConversationFromServer(streamConvId), 50);
                    } else if (activeConversation && addMessageToConversation && !isRagStopped && !assistantSaved) {
                        // Fallback for a backend that does not save server-side
                        // (or a save it could not make). Carries run_id, so the
                        // server returns its own row instead of duplicating it.
                        try {
                            if (savedContent.trim()) {
                                assistantSaved = true; // H-0.5: exactly-one-save guard
                                logger.info('CHAT', 'Saving RAG message to database');
                                const savedAssistant = await addMessageToConversation(activeConversation.id, savedContent, 'assistant', ragMetadata);
                                logger.info('CHAT', 'RAG message saved successfully');
                                if (savedAssistant?.id) {
                                    setMessages(prev => prev.map(msg =>
                                        msg.id === assistantMessageId
                                            ? { ...msg, dbId: savedAssistant.id }
                                            : msg
                                    ));
                                }
                                // Fire-and-Forget: if the response landed in a
                                // conversation the user has since navigated away
                                // from, flag a green dot in the sidebar instead of
                                // updating it silently.
                                if (activeConversation.id !== viewedConvIdRef.current) {
                                    markConversationResponded(activeConversation.id);
                                }
                            }
                        } catch (error) {
                            logger.error('CHAT', 'Error saving RAG message:', error);
                        }
                    }
                },
                async (error) => {
                    if (watchdogTimer) clearTimeout(watchdogTimer);
                    // F-5.2 #3: stop-click race. When the user clicks Stop, the fetch is
                    // aborted, which surfaces here as an AbortError AFTER the stop handler
                    // has already rendered the "*(Stopped)*" bubble. Overwriting it with a
                    // generic error is wrong — the turn was intentionally cancelled, not
                    // failed. Bail if this conversation was stopped or the error is an abort.
                    const isAbort = error?.name === 'AbortError'
                        || /abort/i.test(error?.message || '');
                    if (isRagStopped || isAbort) {
                        logger.info('CHAT', 'RAG stream aborted/stopped — leaving the stopped bubble intact');
                        clearConvToolSteps(streamConvId);
                        markConvProcessing(streamConvId, false);
                        delete ragStopFnsRef.current[streamConvId];
                        return;
                    }
                    logger.error('CHAT', 'RAG streaming error:', error);
                    clearActiveRun();
                    clearConvToolSteps(streamConvId);
                    markConvProcessing(streamConvId, false);
                    delete ragStopFnsRef.current[streamConvId];
                    // #439: classify instead of printing error.message — a refused
                    // request (402 energy, 403 gate) keeps its sentence; a dropped
                    // connection / 5xx renders the standard one with a Retry. Any
                    // text already streamed is KEPT (it used to be overwritten).
                    const streamError = streamErrorFrom(error, !fullContent.trim());
                    const errorContent = fullContent.trim() ? fullContent : streamErrorDisplayText(streamError);
                    const errorMetadata = { has_rag_context: false, error: true, stream_error: streamError };
                    setMessages(prev => prev.map(msg =>
                        msg.id === assistantMessageId
                            ? { ...msg, content: errorContent, isSearching: false, metadata: errorMetadata }
                            : msg
                    ));
                    // #457: once the run started on the server, the server saves
                    // its outcome — a failure on OUR side of the wire (a dropped
                    // connection) must not add a second, error row. Reload what
                    // the server has instead.
                    if (runIdSeen) {
                        assistantSaved = true;
                        setTimeout(() => refreshConversationFromServer(streamConvId), 50);
                        return;
                    }
                    // H-0.5 guard: if the silence watchdog already persisted the
                    // partial answer, don't ALSO save an error row for the turn.
                    if (activeConversation && addMessageToConversation && !isRagStopped && !assistantSaved) {
                        assistantSaved = true;
                        try {
                            await addMessageToConversation(activeConversation.id, errorContent, 'assistant', errorMetadata);
                        } catch (dbError) {
                            console.error('Error saving error message:', dbError);
                        }
                    }
                },
                (payload) => {
                    if (isRagStopped) return;
                    armWatchdog(); // any tool/status event = the stream is alive (H-0.5)
                    if (typeof payload === 'object' && payload.type === 'tool_status') {
                        // H-0.6: steps go into THIS stream's conversation bucket,
                        // never a global array — parallel turns stay isolated.
                        if (payload.event === 'tool_start') {
                            updateConvToolSteps(streamConvId, prev => [...prev, {
                                id: `${payload.tool}_${payload.iteration}_${Date.now()}`,
                                tool: payload.tool,
                                argsSummary: payload.args_summary || '',
                                resultSummary: '',
                                durationMs: 0,
                                iteration: payload.iteration,
                                status: 'running',
                            }]);
                        } else if (payload.event === 'thinking') {
                            updateConvToolSteps(streamConvId, prev => [...prev, {
                                id: `thinking_${payload.iteration}_${Date.now()}`,
                                tool: 'thinking',
                                argsSummary: payload.args_summary || '',
                                resultSummary: '',
                                durationMs: 0,
                                iteration: payload.iteration,
                                status: 'done',
                            }]);
                        } else if (payload.event === 'tool_complete') {
                            updateConvToolSteps(streamConvId, prev => {
                                const updated = [...prev];
                                for (let i = updated.length - 1; i >= 0; i--) {
                                    if (updated[i].tool === payload.tool &&
                                        updated[i].iteration === payload.iteration &&
                                        updated[i].status === 'running') {
                                        updated[i] = {
                                            ...updated[i],
                                            resultSummary: payload.result_summary || '',
                                            durationMs: payload.duration_ms || 0,
                                            status: 'done',
                                        };
                                        break;
                                    }
                                }
                                return updated;
                            });
                        }
                    }
                },
                abortController.signal,
            );

            // H-0.5: arm the silence watchdog once the stream is set up; every
            // token/tool event re-arms it, so it only ever fires on a stream
            // that has genuinely gone quiet — never on a healthy long turn.
            armWatchdog();

            streamStopFunction = stopFunction;
            ragStopFnsRef.current[streamConvId] = () => {
                if (watchdogTimer) clearTimeout(watchdogTimer);
                isRagStopped = true;
                markConvProcessing(streamConvId, false);
                delete ragStopFnsRef.current[streamConvId];
                if (stopFunction) stopFunction();
                setMessages(prev => prev.map(msg =>
                    msg.id === assistantMessageId ? stoppedBubble(msg) : msg
                ));
            };
        } catch (error) {
            logger.error('CHAT', 'Error with RAG setup:', error);
            if (watchdogTimer) clearTimeout(watchdogTimer);
            clearConvToolSteps(streamConvId);
            markConvProcessing(streamConvId, false);
            delete ragStopFnsRef.current[streamConvId];
            setMessages(prev => prev.map(msg =>
                msg.id === assistantMessageId
                    ? (() => {
                        const streamError = streamErrorFrom(error, true);
                        return { ...msg, content: streamErrorDisplayText(streamError), isSearching: false, metadata: { has_rag_context: false, error: true, stream_error: streamError } };
                    })()
                    : msg
            ));
        }
    };

    // F-5.1: stable reply toggle. This MUST be a `useCallback` with an empty deps
    // list — it only uses the functional `setReplyingToMessages(prev => …)` form,
    // so its identity never changes across renders. Passing a fresh inline arrow
    // here (as before) defeated `memo(Message)`'s comparator (which compares
    // `onReply` by identity), forcing every message to re-render on every keystroke
    // and every stream flush. Selection HIGHLIGHTING is now driven by a primitive
    // `isSelectedForReply` computed per-message in ChatMessages, so `onReply` no
    // longer needs to carry the current selection Set.
    // #439: Retry on a failed turn's error notice re-sends the user message that
    // turn was answering, as a fresh turn (exactly what a user typing "try
    // again" was doing by hand). The implementation lives in a ref so the
    // callback handed to every <Message> keeps a STABLE identity (its memo
    // comparator compares it) while still seeing the current thread.
    const retryFailedTurnRef = useRef(null);
    retryFailedTurnRef.current = (failedMsg) => {
        if (!currentConversation || currentConvProcessing) return;
        const idx = messages.findIndex(m => m.id === failedMsg?.id);
        const before = idx >= 0 ? messages.slice(0, idx) : messages;
        const prior = [...before].reverse().find(m => m.isUser);
        if (!prior || !(prior.content || '').trim()) return;
        const attached = prior.attachedDocuments || prior.metadata?.attached_documents || null;
        handleRagMessage(prior.content, prior.display || prior.content, currentConversation,
            false, null, attached && attached.length > 0 ? attached : null);
    };
    const handleRetryFailedTurn = useCallback((failedMsg) => {
        retryFailedTurnRef.current?.(failedMsg);
    }, []);

    const handleReplyToggle = useCallback((msg) => {
        setReplyingToMessages(prev => {
            const key = msg.dbId || msg.id;
            const exists = prev.some(m => (m.dbId || m.id) === key);
            if (exists) return prev.filter(m => (m.dbId || m.id) !== key);
            return [...prev, msg];
        });
    }, []);

    // `voicePatch` (optional) carries audio-component edits for hybrid/voice
    // messages: { voice_segments: [...] } (empty array → message becomes
    // text-only) and/or { voice: null } to clear the legacy single-voice shape.
    // It is persisted via the edit endpoint AND mirrored into local state so the
    // bubble re-renders without a reload.
    const handleEditMessage = async (messageId, dbId, newContent, voicePatch = null) => {
        if (!currentConversation || !dbId) return;
        try {
            // #446 Phantom turns: a run still answering (or stopped but possibly
            // still running server-side) for this conversation must be cancelled
            // BEFORE the edited message is re-run — otherwise it keeps billing
            // and its usage lands in the new answer's receipt. Awaited, so even
            // a timestamp-only cancel is recorded before the new run starts.
            const convId = currentConversation.id;
            const priorRun = activeRunsRef.current[convId];
            if (ragStopFnsRef.current[convId]) ragStopFnsRef.current[convId]();
            if (priorRun) {
                delete activeRunsRef.current[convId];
                try {
                    await apiService.cancelRun(convId, priorRun.userDbId, priorRun.runId);
                } catch (cancelErr) {
                    logger.warn('CHAT', 'cancel-run before edit failed (continuing):', cancelErr);
                }
            }
            const result = await apiService.editMessage(currentConversation.id, dbId, newContent, voicePatch);
            logger.info('CHAT', 'Message edited successfully:', result);
            // Update the PER-CONVERSATION buffer (not just the visible `messages`):
            // the regeneration below reads convMessagesRef via setConvMessages, so a
            // setMessages-only update would be overwritten by the stale buffer and the
            // edited bubble would revert — most visibly its audio players/transcription
            // (UI Refresh Failure for Audio-Based Message Edits bug).
            setConvMessages(currentConversation.id, prev => {
                const messageIndex = prev.findIndex(m => m.id === messageId);
                if (messageIndex === -1) return prev;
                const updatedMessages = prev.slice(0, messageIndex + 1);
                const patched = {
                    ...updatedMessages[messageIndex],
                    content: newContent,
                    updated_at: new Date().toISOString(),
                };
                if (voicePatch && 'voice_segments' in voicePatch) {
                    const segs = voicePatch.voice_segments;
                    patched.voiceSegments = (Array.isArray(segs) && segs.length > 0) ? segs : null;
                }
                if (voicePatch && 'voice' in voicePatch && !voicePatch.voice) {
                    patched.voice = null;
                }
                // The voice/hybrid bubble renders `typedText` (not `content`), so the
                // edited text only shows if we refresh it here too (FE Ignored Text
                // bug #60). Mirror the persisted typed_text patch into local state.
                if (voicePatch && 'typed_text' in voicePatch) {
                    patched.typedText = voicePatch.typed_text || null;
                }
                updatedMessages[messageIndex] = patched;
                return updatedMessages;
            });
            await handleRagMessage(newContent, newContent, currentConversation, true);
        } catch (error) {
            logger.error('CHAT', 'Error editing message:', error);
        }
    };

    // #448 re-open: re-send the trailing user message WITHOUT editing it (its
    // content is unchanged, so no PUT): a new run for the same stored message.
    // `force` = the stamped run went silent — cancel exactly that run first
    // and start a new one regardless; otherwise the server starts it only if
    // no run started meanwhile (`resend_only_if_not_started`).
    const resendPendingTurn = async (msg, { force = false } = {}) => {
        const conv = currentConversation;
        if (!conv || !msg?.dbId) return;
        if (processingConvIdsRef.current.has(conv.id)) return;
        writeResendMarker(msg.dbId);
        setPendingNow(Date.now());
        const stampedRunId = msg.metadata?.run_id || null;
        if (force && stampedRunId) {
            try {
                await apiService.cancelRun(conv.id, msg.dbId, stampedRunId);
            } catch (err) {
                logger.warn('CHAT', 'cancel-run before Resend failed (continuing):', err);
            }
        }
        pendingStopRef.current.delete(conv.id);
        markConvProcessing(conv.id, true);
        const attached = msg.attachedDocuments || msg.metadata?.attached_documents || null;
        const referenced = msg.referencedExpenses || msg.metadata?.referenced_expenses || null;
        await handleRagMessage(
            msg.content, msg.content, conv, true, null,
            attached && attached.length ? attached : null,
            referenced && referenced.length ? referenced : null,
            null, null,
            { onlyIfNotStarted: !force },
        );
    };

    // The automatic re-send — ONCE per message per tab (the sessionStorage
    // marker survives a second reload; the server guard covers a second tab).
    const autoResendKey = pendingView?.autoResend ? pendingUserMsg?.dbId : null;
    useEffect(() => {
        if (!autoResendKey || !pendingUserMsg) return;
        if (readResendMarker(autoResendKey)) return;
        logger.info('CHAT', 'Trailing user message never started a run — re-sending it once');
        resendPendingTurn(pendingUserMsg, { force: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoResendKey]);

    const handleStopStreaming = () => {
        if (canStop) stopStreaming();
        // Only stop the stream for the conversation currently on screen.
        const convId = currentConversation?.id;
        const stopFn = convId && ragStopFnsRef.current[convId];
        if (stopFn) {
            stopFn();
        } else if (convId && processingConvIds.has(convId)) {
            // The turn hasn't registered its stop function yet (it is still
            // saving the user message). Record the intent; handleRagMessage
            // honours it before it starts the stream.
            pendingStopRef.current.add(convId);
            markConvProcessing(convId, false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleSelectConversation = async (conversation) => {
        const convId = conversation.id;
        // ── 1. Atomic, synchronous paint (Issues 2 & 3) ──────────────────────
        // Everything here runs in ONE React commit (event-handler batching), so the
        // header (currentConversation), the message thread (warm buffer or skeleton)
        // and the composer draft appear as a single cohesive unit — no white screen,
        // no staggered pop-in, and the input bar never unmounts. The network fetch
        // is deferred to step 2 (stale-while-revalidate) so it never blocks paint.
        setInChatSearchQuery('');
        inChatSearch.reset();
        selectConversation(conversation);
        viewedConvIdRef.current = convId; // reflect the view immediately (effect also sets it)
        hydrateComposerForConversation(convId); // input/attachments/replies/voice, synchronous
        setIsReadOnly(false); // optimistic — corrected from the fetch below for shared read-only convs

        const buffered = convMessagesRef.current[convId];
        if (buffered !== undefined) {
            // Known buffer (even an empty one, i.e. a genuinely empty conversation)
            // → instant content, no skeleton, no blank. The revalidate in step 2
            // still reconciles it against the DB.
            setMessages(buffered);
            setLoadingConvId(null);
        } else {
            // Cold / unknown → show the skeleton (not the WelcomeScreen / a blank
            // panel) while the fetch runs. mapDbMessages fills it in step 2.
            setMessages([]);
            setLoadingConvId(convId);
        }
        // Mark this conversation most-recently-used and LRU-cap the buffer so it
        // can't grow unboundedly across a long session (F-5.5).
        touchAndPruneConvBuffers(convId);

        if (!activePanel) {
            // Navigating preserves panel state because /chat/:id is a Main Office
            // path; the panel URL already won if one is open. We only navigate
            // to /chat/:id when the user is looking at Main Office (no panel).
            navigate(`/chat/${convId}`, { replace: true });
        }
        try {
            localStorage.setItem('omnis:last-conversation-id', convId);
        } catch (_) { /* ignore */ }

        // ── 2. Background revalidate (never blocks the transition) ────────────
        try {
            const fullConversation = await loadConversation(convId);
            // Bail the reconcile if the user has already navigated elsewhere — we
            // must not clobber the new view with this stale fetch.
            if (viewedConvIdRef.current !== convId) return;
            setIsReadOnly(fullConversation.is_read_only || false);
            if (fullConversation.messages) {
                const chatMessages = mapDbMessages(fullConversation);
                // Prefer the live in-flight buffer over the DB snapshot when this
                // conversation is still processing (its optimistic + streaming
                // messages aren't committed yet) or when the buffer simply has more
                // than the DB has caught up to — the "no ghosting on return" guarantee.
                const buf = convMessagesRef.current[convId];
                const stillProcessing = processingConvIdsRef.current.has(convId);
                const chosen = (buf && (stillProcessing || buf.length > chatMessages.length))
                    ? buf : chatMessages;
                convMessagesRef.current[convId] = chosen;
                setMessages(chosen);
            } else if (!(convMessagesRef.current[convId]?.length)) {
                setMessages([]);
            }
            setLoadingConvId(prev => (prev === convId ? null : prev));
        } catch (error) {
            logger.error('CHAT', 'Error loading conversation:', error);
            setLoadingConvId(prev => (prev === convId ? null : prev));
        }
    };

    const handleCreateConversation = async () => {
        try {
            setIsReadOnly(false);
            // Snapshot BEFORE selectConversation flips the active id: was the
            // composer bound to a real conversation, or to the pre-conversation
            // '__new__' scratch draft?
            const hadActiveConversation = !!currentConversation?.id;
            const newConversation = await createConversation();
            selectConversation(newConversation);
            // Unsent composer content (text + attachments + recorded-but-unsent
            // audio) is exclusively linked to the conversation it originated in.
            // Clicking "New Chat" from an ACTIVE conversation must NOT drag that
            // draft into the fresh thread — it stays with the origin, and the
            // new chat opens with an empty composer (the draft-hydration effect
            // loads the new conversation's own — here empty — draft). Only the
            // '__new__' scratch draft (composed before any conversation existed)
            // migrates into the conversation that adopts it.
            if (!hadActiveConversation) {
                renameDraft(newConversation.id);
            }
            routeToConversation(newConversation.id);
            // Seed an empty buffer + clear any skeleton: a brand-new chat shows the
            // WelcomeScreen instantly, and switching back to it is a warm no-blank load.
            viewedConvIdRef.current = newConversation.id;
            convMessagesRef.current[newConversation.id] = [];
            setLoadingConvId(null);
            setMessages([]);
            clearAttachments();
        } catch (error) {
            console.error('Error creating conversation:', error);
        }
    };

    const handleDeleteConversation = (conversationId) => {
        const conversation = conversations.find(conv => conv.id === conversationId) || currentConversation;
        setConversationToDelete(conversation);
        setShowDeleteDialog(true);
    };

    // Bulk delete wraps the hook so each removed conversation's message buffer is
    // also evicted (F-5.5) — otherwise the buffer leaks their messages for the session.
    // #433: the hook removes the rows optimistically; the buffers are only freed
    // once the server confirmed (a failed delete restores the rows, whose
    // messages must still be there to reopen).
    const handleBulkDeleteConversations = useCallback(async (conversationIds) => {
        const ids = [...(conversationIds || [])];
        if (currentConversation && ids.includes(currentConversation.id)) {
            setMessages([]);
            if (!activePanel) navigate('/', { replace: true });
        }
        try {
            await bulkDeleteConversations(ids);
        } catch (err) {
            setDeleteError(t('msgDeleteConversationsFailed', 'Could not delete those conversations. They have been restored — please try again.'));
            throw err;
        }
        for (const id of ids) {
            convBufferOrderRef.current = removeConvBuffer(
                convMessagesRef.current, convBufferOrderRef.current, id,
            );
        }
    }, [bulkDeleteConversations, currentConversation, activePanel, navigate, setMessages, t]);

    // Inconsistent deletion (#433). The dialog used to stay open — and the row
    // stay in the list — for the whole DELETE round trip (prod: 1-3 s, up to
    // 7.6 s), and a failure was swallowed into the console, closing the dialog
    // with the row still there. Now: close, leave the conversation and drop the
    // row IMMEDIATELY (the hook is optimistic), let the request run behind it,
    // and on a genuine failure the row comes back with a visible error.
    const confirmDeleteConversation = async () => {
        const target = conversationToDelete;
        setShowDeleteDialog(false);
        setConversationToDelete(null);
        if (!target) return;
        setDeleteError(null);
        if (currentConversation?.id === target.id) {
            setMessages([]);
            clearCurrentConversation();
            if (!activePanel) navigate('/', { replace: true });
        }
        try {
            await deleteConversation(target.id);
            // Free the deleted conversation's message buffer (F-5.5) — only once
            // the server confirmed: a failed delete restores the row, and its
            // messages must still be there to reopen it.
            convBufferOrderRef.current = removeConvBuffer(
                convMessagesRef.current, convBufferOrderRef.current, target.id,
            );
        } catch (error) {
            console.error('Error deleting conversation:', error);
            setDeleteError(t(
                'msgDeleteConversationFailed',
                'Could not delete "{name}". It has been restored — please try again.',
                { name: target.title || t('msgDeleteConversationFallbackName', 'this conversation') },
            ));
        }
    };

    const cancelDeleteConversation = () => {
        setShowDeleteDialog(false);
        setConversationToDelete(null);
    };

    const handleJumpToMessage = (messageId) => {
        // A reply references its original message by DB id, but in a live session
        // that message is rendered under its CLIENT id (message.id) — so resolve
        // either to whichever message is actually on screen before scrolling.
        const target = messages.find((m) => m.id === messageId || m.dbId === messageId);
        const domId = target ? target.id : messageId;
        setHighlightedMessageId(domId);
        const messageElement = document.getElementById(`message-${domId}`);
        if (messageElement) {
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => setHighlightedMessageId(null), 2000);
        }
    };

    // #352: the jump used to be `getElementById('search-match-' + i)` against an id
    // minted from a RAW-markdown match count, guarded by `if (el)` — so whenever the
    // two numberings disagreed the arrows moved the counter and nothing else. The
    // hook navigates a list of real DOM nodes instead, so there is nothing to miss.
    const handleSearchQueryChange = (query) => setInChatSearchQuery(query);

    const handleSearchClose = () => {
        setShowSearch(false);
        setInChatSearchQuery('');
        inChatSearch.reset();
    };

    const handleGlobalSearchResult = async (conversationId, messageId, searchTerm, result = null) => {
        // "Conversation misplaced": a Telegram day bucket must open in
        // its OWN channel panel, never here. A DayBlock is a Conversation, so the
        // code below would happily load one into the Main Chat thread — an
        // interface that renders none of the bucket's channel context and that
        // `GET /conversations` deliberately keeps day buckets out of. Deep-link to
        // the panel instead (see utils/searchChannels) and stop.
        const deepLink = channelDeepLink(result, searchTerm);
        if (deepLink) {
            navigate(deepLink);
            return;
        }
        if (currentConversation?.id !== conversationId) {
            const conversation = conversations.find(c => c.id === conversationId);
            if (conversation) {
                await handleSelectConversation(conversation);
            } else {
                try {
                    const fullConv = await loadConversation(conversationId);
                    if (fullConv) {
                        selectConversation(fullConv);
                        viewedConvIdRef.current = conversationId;
                        setLoadingConvId(null);
                        setIsReadOnly(fullConv.is_read_only || false);
                        if (!activePanel) {
                            navigate(`/chat/${conversationId}`, { replace: true });
                        }
                        if (fullConv.messages) {
                            const chatMessages = mapDbMessages(fullConv);
                            // Seed the per-conversation buffer (same contract as
                            // handleSelectConversation) so this load path doesn't
                            // bypass convMessagesRef.
                            convMessagesRef.current[conversationId] = chatMessages;
                            setMessages(chatMessages);
                        }
                    }
                } catch (err) {
                    logger.error('CHAT', 'Error loading conversation from global search:', err);
                    return;
                }
            }
        }
        // Opening a result ALWAYS initiates the in-chat search for the same term
        // (#353 requirement 5). It used to require a `messageId` too, so clicking a
        // conversation's NAME opened the chat and left the user to re-type the term
        // they had just searched for — the search bar didn't even open. With the
        // query set, `useInChatSearch` collects the highlights and scrolls to the
        // FIRST one on its own (its "new term resolves" effect), which is the
        // focus/highlight half of the requirement — nothing to do here.
        //
        // Ordering is load-bearing: the conversation load above is awaited BEFORE
        // the query is set, because MessageSearch resets its own input whenever
        // `conversationId` changes and would otherwise clear the term we just set.
        if (searchTerm) {
            setInChatSearchQuery(searchTerm);
            setShowSearch(true);
        }
        // A jump only applies to a MESSAGE-level result (a name match has no
        // message to jump to — it lands on the first highlight instead).
        if (messageId) {
            setTimeout(() => handleJumpToMessage(messageId), 300);
        }
    };

    // Load conversation from URL on mount (or fall back to last-used from storage)
    //
    // Note: we also fire when the URL conv differs from the currently-loaded
    // one — this is what makes deep-link navigations work (e.g. clicking a
    // "Shared Conversations" row in the Agenda panel jumps via useNavigate,
    // and we want the chat thread to swap even if another conv was already
    // selected). Without the id-mismatch check, the effect bailed because
    // `currentConversation` was already truthy.
    useEffect(() => {
        // Gate on "conversations have finished loading" (not "the list is
        // non-empty"). A SHARED conversation opened from the Agenda isn't in the
        // user's own list, so the old `length > 0` guard meant it never loaded
        // when the user had no/owned conversations — the shared-conversation
        // navigation bug. With the list loaded, an id not found locally falls
        // through to loadConversation() below, which fetches shared convs too.
        const needsLoad = urlConversationId
            && !conversationsLoading
            && (!currentConversation || currentConversation.id !== urlConversationId);
        if (needsLoad) {
            const conversation = conversations.find(c => c.id === urlConversationId);
            if (conversation) {
                handleSelectConversation(conversation);
            } else {
                loadConversation(urlConversationId).then(fullConv => {
                    if (fullConv) {
                        selectConversation(fullConv);
                        viewedConvIdRef.current = urlConversationId;
                        setLoadingConvId(null);
                        setIsReadOnly(fullConv.is_read_only || false);
                        if (fullConv.messages) {
                            const chatMessages = mapDbMessages(fullConv);
                            // Seed the per-conversation buffer (same contract as
                            // handleSelectConversation) so this deep-link load
                            // path doesn't bypass convMessagesRef.
                            convMessagesRef.current[urlConversationId] = chatMessages;
                            setMessages(chatMessages);
                        }
                    }
                }).catch(() => {
                    if (!activePanel) navigate('/', { replace: true });
                });
            }
        } else if (!urlConversationId && !currentConversation && conversations.length > 0) {
            // No URL conv and none selected — try restore last-used from storage
            try {
                const lastId = localStorage.getItem('omnis:last-conversation-id');
                if (lastId) {
                    const conv = conversations.find(c => c.id === lastId);
                    if (conv) handleSelectConversation(conv);
                }
            } catch (_) { /* ignore */ }
        }
    }, [urlConversationId, conversations, currentConversation, conversationsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keyed on the conversation id, not the whole `currentConversation` object —
    // the object identity changes on every activity refetch, which would re-map and
    // clobber live messages; we only want to seed messages when the id changes.
    useEffect(() => {
        if (currentConversation && currentConversation.messages && !isLoading && messages.length === 0) {
            const chatMessages = mapDbMessages(currentConversation);
            setMessages(chatMessages);
        }
    }, [currentConversation?.id, setMessages, isLoading, messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // H-0.6: attach live tool steps to the on-screen "searching" bubble using
        // ONLY the on-screen conversation's own steps — a background stream's
        // events never leak into the thread the user is looking at.
        // Routed through setConvMessages (not the bare useChat setMessages) so the
        // live steps also land in the per-conversation ref buffer — otherwise the
        // token callback (which reads that buffer) would clobber `toolSteps` on the
        // first content token and the #240 timer header would flicker out mid-stream.
        if (currentConvProcessing && currentConversation?.id) {
            const liveSteps = ragToolStepsByConv[currentConversation.id] || [];
            setConvMessages(currentConversation.id, prev => prev.map(msg =>
                msg.isSearching ? { ...msg, toolSteps: liveSteps } : msg
            ));
        }
    }, [ragToolStepsByConv, currentConvProcessing, currentConversation?.id, setConvMessages]);

    const removeReferencedExpense = (expenseId) => {
        setReferencedExpenses((prev) => prev.filter((r) => r.expense_id !== expenseId));
    };

    // #290: conversations that must stay visible in the sidebar regardless of
    // its "hide untitled empties" filter — anything mid-turn (streaming) or
    // holding a queued voice send. Without this, the flush's draft-clear hid a
    // brand-new conversation for the whole dispatch window ("vanishes from the
    // sidebar as if it was never created") until its generated title landed.
    /* ── Slot geometry (#359/#360/#361) ───────────────────────────────────── */
    const chatCovered = chatOpen && !!expandedOccupant;
    const visibleOccupantCount = (chatOpen ? 1 : 0) + featurePanels.length;
    // The conversation column belongs to the chat. It goes when the chat is closed,
    // and when an expanded occupant covers everything right of the Features Sidebar.
    // #432: when the column cannot fit beside the chat (and a panel), it folds
    // away automatically — and so does its "show" arrow, which would otherwise be
    // a control that cannot do anything at this width. Closing the panel (or
    // widening the window) brings the column straight back.
    const columnFits = conversationColumnFits(viewWidth, featurePanels.length > 0);
    const conversationColumnVisible = !isMobile && chatOpen && !expandedOccupant && showSidebar && columnFits;
    const conversationColumnHidden = chatOpen && !expandedOccupant && !showSidebar && columnFits;

    const pinnedConversationIds = useMemo(() => {
        const pinned = new Set();
        processingConvIds.forEach((id) => pinned.add(id));
        pendingSendConvIds.forEach((id) => { if (id !== '__new__') pinned.add(id); });
        return pinned;
    }, [processingConvIds, pendingSendConvIds]);

    return (
        <div ref={viewRef} data-testid="main-office-view" className="flex h-full min-w-0 shell:gap-[14px] overflow-hidden">
            {/* The conversation column is the CHAT occupant's own furniture: it is
                there only while the chat is open AND visible. An expanded feature
                covers everything right of the Features Sidebar (#359), and a closed
                chat takes its column with it — which is also what frees the ~314px
                a second feature needs to meet its 380px floor. */}
            {conversationColumnVisible && (
                <ConversationSidebar
                    onHide={() => setShowSidebar(false)}
                    conversations={conversations}
                    currentConversation={currentConversation}
                    pinnedConversationIds={pinnedConversationIds}
                    onSelectConversation={handleSelectConversation}
                    onCreateConversation={handleCreateConversation}
                    onDeleteConversation={handleDeleteConversation}
                    onBulkDeleteConversations={handleBulkDeleteConversations}
                    onLoadAllConversations={loadAllConversations}
                    onUpdateConversation={updateConversation}
                    loading={conversationsLoading}
                    onSearchResultClick={handleGlobalSearchResult}
                />
            )}

            {/* #360 — with the column hidden, its arrow stays parked against the
                Features Sidebar so it is always obvious how to get the column back.
                Rendered where the column would be, so it never overlaps content. */}
            {conversationColumnHidden && (
                <div className="hidden shell:flex flex-none items-start pt-[22px]">
                    <button
                        type="button"
                        data-testid="conversation-sidebar-show"
                        onClick={() => setShowSidebar(true)}
                        title={t('navShowConversations', 'Show conversations')}
                        aria-label={t('navShowConversations', 'Show conversations')}
                        className="pai-icon-btn w-[30px] h-[46px]"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
            )}

            <div ref={splitContainerRef} className="relative flex-1 flex min-w-0">
            <div
                ref={chatPaneRef}
                data-testid="chat-drop-zone"
                // The chat is NEVER unmounted — every in-flight turn, per-conversation
                // buffer, draft and origin-bound upload lives in this subtree — so it
                // has two hidden states, and they are different on purpose:
                //
                //   COVERED (an expanded occupant, #359) → `invisible`. The expanded
                //     panel is translucent glass laid over this pane, so the chat read
                //     straight through it; hiding it reveals the WALLPAPER as designed
                //     while the pane keeps its box, and therefore its scroll position.
                //   CLOSED (the chat occupant itself, #361) → `hidden`. It must give up
                //     its space entirely, which `visibility` cannot do. `display:none`
                //     destroys the scrollport, so the scroll offset is saved and
                //     restored around the transition (see the effect above).
                aria-hidden={!chatOpen || chatCovered ? 'true' : undefined}
                className={`flex flex-col flex-1 min-w-0 relative shell:pai-panel overflow-hidden ${
                    !chatOpen ? 'hidden' : (chatCovered ? 'invisible' : '')
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={(e) => {
                    // Expense-ref drops from ExpensesPanel — consume before
                    // the file-drop handler so no spurious upload attempt happens.
                    const refData = e.dataTransfer.getData('application/x-expense-ref');
                    if (refData) {
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                            const parsed = JSON.parse(refData);
                            window.dispatchEvent(new CustomEvent('omnis:expense-ref-dropped', { detail: parsed }));
                        } catch (_) { /* ignore */ }
                        return;
                    }
                    handleDrop(e);
                }}
            >
                {isDragging && (
                    <div className="absolute inset-0 bg-teal-500 bg-opacity-50 flex items-center justify-center z-50 pointer-events-none rounded-lg border-2 border-dashed border-teal-300">
                        <div className="text-center">
                            <div className="text-white text-2xl font-bold">{t('msgDropFilesToUpload', 'Drop files to upload')}</div>
                            <div className="text-teal-100 text-sm mt-1">{t('msgDropFilesHint', 'Files will be indexed and added to your library')}</div>
                        </div>
                    </div>
                )}
                {isUploading && (
                    <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-teal-600 text-white px-4 py-2 rounded-lg shadow-modal-sm text-sm z-50 flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                        {t('msgUploadingAndIndexing', 'Uploading and indexing...')}
                    </div>
                )}

                <Header
                    apiStatus={apiStatus}
                    onDeleteConversation={() => currentConversation && handleDeleteConversation(currentConversation.id)}
                    onToggleSidebar={isMobile ? () => onOpenMobileMenu?.() : () => setShowSidebar(!showSidebar)}
                    menuTestId={isMobile ? 'mobile-menu-button' : 'sidebar-toggle'}
                    menuTitle={isMobile ? t('menu', 'Menu') : undefined}
                    // Desktop lost its hamburger: the conversation column owns its own
                    // arrow now (#360). Mobile keeps the button — it is the overlay
                    // menu's only entry point (#171).
                    showMenuButton={isMobile}
                    menuHasUnseen={isMobile && unseenPanels.length > 0}
                    onCloseChat={!isMobile ? onCloseChat : undefined}
                    onSearchClick={() => setShowSearch(!showSearch)}
                    onShareClick={() => setShowShareDialog(true)}
                    showSidebar={showSidebar}
                    currentConversationTitle={currentConversation?.title}
                    hasCurrentConversation={!!currentConversation}
                    isReadOnly={isReadOnly}
                />

                <MessageSearch
                    conversationId={currentConversation?.id}
                    isOpen={showSearch}
                    onClose={handleSearchClose}
                    onNext={inChatSearch.next}
                    onPrevious={inChatSearch.previous}
                    onSearchQueryChange={handleSearchQueryChange}
                    totalMatches={inChatSearch.totalMatches}
                    currentMatchIndex={inChatSearch.activeIndex}
                    initialQuery={inChatSearchQuery}
                />

                <div className="relative flex-1 min-h-0">
                  <div ref={chatScrollRef} onScroll={handleChatScroll} data-testid="chat-scroll" className="h-full overflow-y-auto">
                    <ChatMessages
                        messages={messages}
                        attachedFiles={attachedFiles}
                        onPromptSelect={setInput}
                        userInitials={userInitials}
                        userName={user?.username}
                        messagesEndRef={messagesEndRef}
                        highlightedMessageId={highlightedMessageId}
                        conversationId={currentConversation?.id}
                        onEditMessage={handleEditMessage}
                        onJumpToMessage={handleJumpToMessage}
                        onReply={!isReadOnly ? handleReplyToggle : undefined}
                        replyingToMessages={replyingToMessages}
                        isReadOnly={isReadOnly}
                        searchQuery={inChatSearchQuery}
                        isLoadingConversation={!!loadingConvId && loadingConvId === (currentConversation?.id || null) && messages.length === 0}
                        onRetry={!isReadOnly ? handleRetryFailedTurn : null}
                        retryDisabled={currentConvProcessing}
                        pendingTurn={pendingView ? {
                            status: pendingView.status,
                            onResend: () => resendPendingTurn(pendingUserMsg, { force: pendingView.force }),
                        } : null}
                    />
                  </div>
                  {/* Floating "Scroll to bottom" — appears only when scrolled up
                      past the threshold; snaps back to the latest message. */}
                  {showScrollDown && (
                    <button
                        type="button"
                        onClick={scrollChatToBottom}
                        title={t('msgScrollToLatest', 'Scroll to latest message')}
                        aria-label={t('msgScrollToLatest', 'Scroll to latest message')}
                        className="absolute bottom-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/[0.06] text-pai-secondary text-white border border-pai shadow-modal-sm hover:bg-white/[0.07] transition-colors"
                    >
                        <ChevronDown size={20} />
                    </button>
                  )}
                </div>

                {isReadOnly ? (
                    <div className="bg-white/[0.06] border-t border-pai-subtle p-4 text-center">
                        <p className="text-sm text-pai-meta">
                            {t('msgSharedReadOnly', 'This is a shared conversation. You have read-only access.')}
                        </p>
                    </div>
                ) : (
                    <ChatInput
                        input={input}
                        setInput={setInput}
                        attachedFiles={attachedFiles}
                        onRemoveAttachment={handleRemoveAttachment}
                        onClearAttachments={handleClearAllAttachments}
                        onSendMessage={handleSendMessage}
                        onKeyPress={handleKeyPress}
                        isLoading={isLoading}
                        apiStatus={apiStatus}
                        canStop={canStop || currentConvProcessing}
                        onStopStreaming={handleStopStreaming}
                        replyingToMessages={replyingToMessages}
                        onCancelReply={() => setReplyingToMessages([])}
                        onRemoveReply={(msg) => setReplyingToMessages(prev => prev.filter(m => (m.dbId || m.id) !== (msg.dbId || msg.id)))}
                        onPasteFiles={handleDroppedFiles}
                        isRecording={isRecording}
                        recordingDuration={recordingDuration}
                        onStartRecording={handleStartRecording}
                        onAcceptRecording={handleAcceptRecording}
                        onDiscardRecording={handleDiscardRecording}
                        voiceSegments={voiceSegments}
                        onRemoveVoiceSegment={handleRemoveSegment}
                        onToggleSegmentTranscription={handleToggleSegmentTranscription}
                        onEditSegmentTranscription={handleEditSegmentTranscription}
                        dictationMode
                        isDictating={currentDictating}
                        dictationNotice={
                            dictationNotice && dictationNotice.key === (currentConversation?.id || '__new__')
                                ? dictationNotice
                                : null
                        }
                        onDismissDictationNotice={() => setDictationNotice(null)}
                        pendingSend={currentPendingSend}
                        referencedExpenses={referencedExpenses}
                        onRemoveReferencedExpense={removeReferencedExpense}
                        onAddFiles={handleDroppedFiles}
                        onOpenFilePicker={() => setChatDocPickerOpen(true)}
                    />
                )}
            </div>

            {/* Occupant slots — inline columns in split mode, an absolute overlay
                for an expanded one. Everything stays mounted underneath, so
                collapsing back never reloads anything.

                IMPORTANT: every wrapper is KEYED BY OCCUPANT NAME, not by slot
                position. That is what lets a panel survive its neighbours coming
                and going: when the chat closes and a second feature moves in, the
                first feature's element merely changes index in this array, and
                React moves the existing DOM node instead of remounting the panel
                (which would wipe its internal navigation state — the "Set in
                stone" bug). Never re-introduce a mode-conditional branch that
                renders the same panel from two different JSX positions. */}
            {featurePanels.map(({ name, node }, idx) => {
                const isExpanded = expandedOccupant === name;
                // Only the RIGHTMOST of two occupants is sized by the divider;
                // whatever is left of it takes the rest, subject to its own floor.
                // A lone occupant is simply the whole area.
                const isRatioSized = visibleOccupantCount > 1 && idx === featurePanels.length - 1;
                const hasNeighbourBefore = idx > 0 || chatOpen;
                return (
                    <React.Fragment key={name}>
                        {hasNeighbourBefore && !expandedOccupant && (
                            <SplitDivider
                                containerRef={splitContainerRef}
                                onRatioChange={onSplitRatioChange}
                            />
                        )}
                        <div
                            data-testid={`panel-slot-${name}`}
                            data-expanded={isExpanded ? 'true' : 'false'}
                            /* Opacity #362 (third re-open): `animate-fade-slide-in`
                               must NOT be `forwards`. A forwards-filled OPACITY
                               animation keeps this wrapper a composited render
                               surface = a backdrop root, which silently kills the
                               `backdrop-filter: blur(22px)` on the `.pai-panel`
                               inside it — the expanded panel then showed the raw
                               wallpaper through a 3% veil while the split panel
                               (transform-only animation) blurred correctly. See
                               the comment on `.animate-fade-slide-in` in index.css. */
                            className={isExpanded
                                ? 'absolute inset-0 z-20 animate-fade-slide-in'
                                : `h-full animate-slide-in-right ${isRatioSized ? 'flex-none' : 'flex-1'} ${
                                    expandedOccupant ? 'invisible' : ''
                                }`}
                            style={isExpanded
                                ? undefined
                                : !isRatioSized
                                ? { minWidth: `${PANEL_MIN_WIDTH}px` }
                                : {
                                    width: `${Math.round(splitRatio * 100 * 100) / 100}%`,
                                    /* The occupant on the LEFT keeps a hard floor, and which
                                       floor depends on who it is. `splitRatio` is a FRACTION of
                                       the split container, and the PAI redesign shrank that
                                       container by ~230px (a 250px nav sidebar where a 64px icon
                                       rail used to be, plus the 14px frame and gutters) — so the
                                       same 0.75 max ratio that once left a 240px chat column now
                                       leaves ~154px, a sliver too narrow to read or compose in.
                                       Capping the PANEL instead of lowering the ratio keeps the
                                       divider's full travel on wide screens and only bites at the
                                       narrow end. CHAT_MIN_WIDTH is the width every #243
                                       containment contract is written against.

                                       feature|feature (#361) raises that floor to a panel's own
                                       380px — and it FITS only because a closed chat takes the
                                       300px conversation column with it: the split container goes
                                       674 → 988 at the shell's 1280px minimum, and
                                       380 + 14 + 380 = 774 ≤ 988. If the conversation column ever
                                       survives a closed chat, this is the arithmetic that breaks. */
                                    maxWidth: `calc(100% - ${(chatOpen ? CHAT_MIN_WIDTH : PANEL_MIN_WIDTH) + DIVIDER_WIDTH}px)`,
                                    /* …and the panel has a floor for the same reason. A panel is a
                                       real application surface, not a sliver: the Kanban header,
                                       for one, carries a fixed cost (back + name, then members /
                                       kebab / expand / close) that cannot share a row with a
                                       usable search below ~380px. */
                                    minWidth: `${PANEL_MIN_WIDTH}px`,
                                }}
                        >
                            {node}
                        </div>
                    </React.Fragment>
                );
            })}
            </div>

            {deleteError && createPortal(
                <div
                    role="alert"
                    data-testid="conversation-delete-error"
                    className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] max-w-[calc(100vw-32px)] pai-panel px-4 py-3 text-sm text-white flex items-center gap-3"
                >
                    <span className="min-w-0 break-words">{deleteError}</span>
                    <button
                        type="button"
                        onClick={() => setDeleteError(null)}
                        className="pai-icon-btn shrink-0"
                        aria-label={t('commonClose', 'Close')}
                    >
                        <X size={14} />
                    </button>
                </div>,
                document.body,
            )}

            <ConfirmationDialog
                isOpen={showDeleteDialog}
                onClose={cancelDeleteConversation}
                onConfirm={confirmDeleteConversation}
                title={t('msgDeleteConversationTitle', 'Delete Conversation')}
                message={t('msgDeleteConversationMessage', 'Are you sure you want to delete "{name}"?\n\nThis action cannot be undone.', { name: conversationToDelete?.title || t('msgDeleteConversationFallbackName', 'this conversation') })}
                confirmText={t('commonDelete', 'Delete')}
                cancelText={t('commonCancel', 'Cancel')}
                type="danger"
            />

            <ShareDialog
                isOpen={showShareDialog}
                onClose={() => setShowShareDialog(false)}
                conversationId={currentConversation?.id}
            />

            {uploadedDocModal && (
                <CreatedDocumentModal
                    documentId={uploadedDocModal.id}
                    documentName={uploadedDocModal.name}
                    onClose={() => handleUploadedDocAction('closed')}
                    onDocumentUpdated={handleUploadedDocAction}
                />
            )}

            {duplicateQueue.length > 0 && (
                <RenameDuplicateModal
                    key={duplicateQueue[0].tempId}
                    filename={duplicateQueue[0].filename}
                    message={
                        duplicateQueue.length > 1
                            ? t('msgDuplicateBatchSuffix', '{message} ({n} files in this batch need a unique name)', { message: duplicateQueue[0].message, n: duplicateQueue.length })
                            : duplicateQueue[0].message
                    }
                    onSubmit={handleRenameDuplicate}
                    onCancel={handleCancelDuplicate}
                />
            )}

            {/* "Select from File Manager" picker for the composer "+" menu (#208).
                Reuses the shared AttachPickerModal (#117) with chat-scoped testids;
                selecting a doc attaches it WITHOUT re-uploading. */}
            {chatDocPickerOpen && (
                <AttachPickerModal
                    title={t('msgSelectFromFileManager', 'Select from File Manager')}
                    testIds={{ modal: 'chat-attach-picker', search: 'chat-attach-picker-search', item: 'chat-attach-picker-item' }}
                    onClose={() => setChatDocPickerOpen(false)}
                    onSelect={(id, doc) => {
                        handleSelectExistingDocument(doc);
                        setChatDocPickerOpen(false);
                    }}
                />
            )}

            {/* App-like overlay menu — mobile only (Mobile Visual #171). Selecting
                a feature or a conversation closes the overlay and pops the choice
                into the full-screen view. */}
            {/* #432 — the conversation sheet (mobile shell only). The same
                ConversationSidebar as the desktop column, full screen: its own
                "hide" arrow closes it, and choosing a conversation or a search
                hit closes it and shows the chat. */}
            {isMobile && conversationSheetOpen && createPortal(
                <div data-testid="conversation-sheet" className="fixed inset-0 z-[45] flex bg-pai-modal">
                    <ConversationSidebar
                        variant="sheet"
                        onHide={() => setConversationSheetOpen(false)}
                        conversations={conversations}
                        currentConversation={currentConversation}
                        pinnedConversationIds={pinnedConversationIds}
                        onSelectConversation={(conv) => { setConversationSheetOpen(false); handleSelectConversation(conv); }}
                        onCreateConversation={(...args) => { setConversationSheetOpen(false); return handleCreateConversation(...args); }}
                        onDeleteConversation={handleDeleteConversation}
                        onBulkDeleteConversations={handleBulkDeleteConversations}
                        onLoadAllConversations={loadAllConversations}
                        onUpdateConversation={updateConversation}
                        loading={conversationsLoading}
                        onSearchResultClick={(...args) => { setConversationSheetOpen(false); return handleGlobalSearchResult(...args); }}
                    />
                </div>,
                document.body,
            )}

            {isMobile && (
                <MobileMenuOverlay
                    open={mobileMenuOpen}
                    onClose={() => onCloseMobileMenu?.()}
                    activePanel={activePanelName}
                    unseenPanels={unseenPanels}
                    onSelectFeature={(name) => { onCloseMobileMenu?.(); onTogglePanel?.(name); }}
                    conversations={conversations}
                    currentConversationId={currentConversation?.id || null}
                    onSelectConversation={(conv) => { onCloseMobileMenu?.(); handleSelectConversation(conv); }}
                    onManageConversations={() => { onCloseMobileMenu?.(); setConversationSheetOpen(true); }}
                    onNewChat={() => { onCloseMobileMenu?.(); handleCreateConversation(); }}
                    onOpenSettings={onOpenSettings}
                    onOpenLauncher={onOpenLauncher}
                    user={user}
                    onLogout={onLogout}
                />
            )}
        </div>
    );
}
